import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Users, DollarSign, Video, FileText, Settings,
  BarChart3, Upload, Sparkles, Send, Check, Loader2, Mail, Pin,
  Globe, Headphones, Book, BookOpen, Plus, Trash2, TrendingUp,
  TrendingDown, MessageSquare, Heart, Eye, Clock, Star, Filter,
  Calendar, Target, Brain, Lightbulb, AlertCircle, CreditCard,
  Search, ChevronDown, ChevronUp, Play, ThumbsUp, ThumbsDown, Pencil,
  RefreshCw, Download, Zap, Award, UserCheck, UserX, Activity, Gift, Copy,
  Mic, Grid, List, Edit, X, MapPin, Link, Phone, User, Crown, Image, Camera
} from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent } from '../ui/tabs';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import AnalyticsTab from './tabs/AnalyticsTab';
import EmailTab from './tabs/EmailTab';
import {
  htmlToPlainText,
  normalizeEmailBodyHtml,
} from './email/emailBodyFormatters';
import FeedbackTab from './tabs/FeedbackTab';
import GemsTab from './GemsTab';
import GuestPassesTab from './tabs/GuestPassesTab';
import GuestGuidesTab from './tabs/GuestGuidesTab';
import MembersTab from './tabs/MembersTab';
import MonitoringTab from './tabs/MonitoringTab';
import DocumentsTab from './tabs/DocumentsTab';
import FeaturePlanningTab from './tabs/FeaturePlanningTab';
import PeerSupportTab from './PeerSupportTab';
import PricingTab from './tabs/PricingTab';
import PromptsTab from './tabs/PromptsTab';
import RecordingsTab from './tabs/RecordingsTab';
import ResourcesTab from './tabs/ResourcesTab';
import SiteSettingsTab from './tabs/SiteSettingsTab';
import SupportPartnerContent from '../support/SupportPartnerContent';
import AdvisoryContent from '../advisory/AdvisoryContent';
import SupportersTab from './tabs/SupportersTab';
import SupportPartnerAccountsTab from './tabs/SupportPartnerAccountsTab';
import AdvisoryPartnerAccountsTab from './tabs/AdvisoryPartnerAccountsTab';
import SuperFanAccountsTab from './tabs/SuperFanAccountsTab';
import ComingSoonTab from './tabs/ComingSoonTab';
import MarketingContentTab from './tabs/MarketingContentTab';
import MarketingIntelligenceTab from './tabs/MarketingIntelligenceTab';
import MarketingPlaylistTab from './tabs/MarketingPlaylistTab';
import MembershipEnrollmentsTab from './tabs/MembershipEnrollmentsTab';
import CommandCenterLanding from './CommandCenterLanding';
import ActiveRoomContext from './ActiveRoomContext';
import {
  ADMIN_ROOMS,
  COMMAND_CENTER_HOME_TAB,
  DEFAULT_ROOM_ID,
  getAdminRoomById,
  getPrimaryRoomForTab,
} from './adminRoomConfig';
import {
  ADMIN_ZONES,
  getAdminZoneById,
  getAdminZoneForRoomOrTab,
} from './adminZoneConfig';
import { buildActiveRoomContext, buildActiveZoneContext } from './adminRoomInsights';
import { deriveGuidePartnersState } from './guidePartnersRoomModel';
import { readTokenFromStorage, useAdminFetch } from './hooks/useAdminFetch';
import { useCommandCenterQueueState } from './hooks/useCommandCenterQueueState';
import { useMembersAdminState } from './hooks/useMembersAdminState';
import { useSiteSettingsAdminState } from './hooks/useSiteSettingsAdminState';
import { useAuth } from '../../context/AuthContext';
import Header from '../Layout/Header';
import { toast } from 'sonner';

const RECORDING_MAX_UPLOAD_BYTES = 2 * 1024 * 1024 * 1024;
const RECORDING_UPLOAD_TIMEOUT_MS = 20 * 60 * 1000;

const FOCUS_AREA_ORDER = ['parenting', 'marriage', 'career', 'general'];
const getFocusAreaLabel = (id) => (id === 'general' ? 'Open Discussion' : (id ? id.charAt(0).toUpperCase() + id.slice(1) : ''));
const EMPTY_RESOURCE_FORM = {
  category: '',
  name: '',
  mediaType: '',
  url: '',
  author: '',
  description: '',
  amazonUrl: '',
  recommendationNote: '',
};
const EMPTY_RESOURCE_EDIT_FORM = {
  name: '',
  url: '',
  author: '',
  description: '',
  mediaType: '',
  amazonUrl: '',
  recommendationNote: '',
};

const ROOM_NAV_ACCENTS = {
  'reach-dads': {
    icon: 'bg-orange-100 text-orange-700 dark:bg-orange-500/10 dark:text-orange-300',
    active: 'border-orange-200 bg-orange-50/90 text-orange-950 shadow-lg dark:border-orange-500/30 dark:bg-orange-500/10 dark:text-white',
    badge: 'border-orange-200 bg-orange-100/80 text-orange-700 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300',
  },
  'support-dads': {
    icon: 'bg-cyan-100 text-cyan-800 dark:bg-aqua/10 dark:text-aqua',
    active: 'border-cyan-200 bg-cyan-50/90 text-cyan-950 shadow-lg dark:border-aqua/30 dark:bg-aqua/10 dark:text-white',
    badge: 'border-cyan-200 bg-cyan-100/80 text-cyan-700 dark:border-aqua/20 dark:bg-aqua/10 dark:text-aqua',
  },
  'guide-partners': {
    icon: 'bg-amber-100 text-amber-700 dark:bg-amber-400/10 dark:text-amber-300',
    active: 'border-amber-200 bg-amber-50/90 text-amber-950 shadow-lg dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-white',
    badge: 'border-amber-200 bg-amber-100/80 text-amber-700 dark:border-amber-400/20 dark:bg-amber-400/10 dark:text-amber-300',
  },
  'advisory-partners': {
    icon: 'bg-lime-100 text-lime-700 dark:bg-lime-400/10 dark:text-lime-300',
    active: 'border-lime-200 bg-lime-50/90 text-lime-950 shadow-lg dark:border-lime-400/30 dark:bg-lime-400/10 dark:text-white',
    badge: 'border-lime-200 bg-lime-100/80 text-lime-700 dark:border-lime-400/20 dark:bg-lime-400/10 dark:text-lime-300',
  },
  'support-partners': {
    icon: 'bg-pink-100 text-pink-700 dark:bg-pink-400/10 dark:text-pink-300',
    active: 'border-pink-200 bg-pink-50/90 text-pink-950 shadow-lg dark:border-pink-400/30 dark:bg-pink-400/10 dark:text-white',
    badge: 'border-pink-200 bg-pink-100/80 text-pink-700 dark:border-pink-400/20 dark:bg-pink-400/10 dark:text-pink-300',
  },
  'super-fans': {
    icon: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-400/10 dark:text-yellow-300',
    active: 'border-yellow-200 bg-yellow-50/90 text-yellow-950 shadow-lg dark:border-yellow-400/30 dark:bg-yellow-400/10 dark:text-white',
    badge: 'border-yellow-200 bg-yellow-100/80 text-yellow-700 dark:border-yellow-400/20 dark:bg-yellow-400/10 dark:text-yellow-300',
  },
  marketing: {
    icon: 'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-400/10 dark:text-fuchsia-300',
    active: 'border-fuchsia-200 bg-fuchsia-50/90 text-fuchsia-950 shadow-lg dark:border-fuchsia-400/30 dark:bg-fuchsia-400/10 dark:text-white',
    badge: 'border-fuchsia-200 bg-fuchsia-100/80 text-fuchsia-700 dark:border-fuchsia-400/20 dark:bg-fuchsia-400/10 dark:text-fuchsia-300',
  },
  'free-users': {
    icon: 'bg-violet-100 text-violet-700 dark:bg-violet-400/10 dark:text-violet-300',
    active: 'border-violet-200 bg-violet-50/90 text-violet-950 shadow-lg dark:border-violet-400/30 dark:bg-violet-400/10 dark:text-white',
    badge: 'border-violet-200 bg-violet-100/80 text-violet-700 dark:border-violet-400/20 dark:bg-violet-400/10 dark:text-violet-300',
  },
  'listen-learn': {
    icon: 'bg-sky-100 text-sky-700 dark:bg-sky-400/10 dark:text-sky-300',
    active: 'border-sky-200 bg-sky-50/90 text-sky-950 shadow-lg dark:border-sky-400/30 dark:bg-sky-400/10 dark:text-white',
    badge: 'border-sky-200 bg-sky-100/80 text-sky-700 dark:border-sky-400/20 dark:bg-sky-400/10 dark:text-sky-300',
  },
  'steward-the-work': {
    icon: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300',
    active: 'border-emerald-200 bg-emerald-50/90 text-emerald-950 shadow-lg dark:border-emerald-400/30 dark:bg-emerald-400/10 dark:text-white',
    badge: 'border-emerald-200 bg-emerald-100/80 text-emerald-700 dark:border-emerald-400/20 dark:bg-emerald-400/10 dark:text-emerald-300',
  },
  'system-health': {
    icon: 'bg-slate-100 text-slate-700 dark:bg-slate-400/10 dark:text-slate-300',
    active: 'border-slate-200 bg-slate-100 text-slate-900 shadow-lg dark:border-slate-400/25 dark:bg-slate-400/10 dark:text-white',
    badge: 'border-slate-200 bg-slate-100 text-slate-700 dark:border-slate-400/20 dark:bg-slate-400/10 dark:text-slate-300',
  },
};

const ROOM_PILL_DOT_CLASSES = {
  urgent: 'bg-red-400 dark:bg-red-300',
  attention: 'bg-yellow-400 dark:bg-yellow-300',
  next: 'bg-aqua',
  steady: 'bg-slate-400 dark:bg-slate-300',
};

const ACTIVE_ZONE_PILL_CLASS = 'border-cyan-200 bg-cyan-50 text-cyan-950 shadow-[0_14px_35px_-24px_rgba(8,145,178,0.65)] ring-1 ring-cyan-200/70 dark:border-aqua/40 dark:bg-[#11161d] dark:text-white dark:ring-aqua/20';

const ZONE_STATUS_PRIORITY = {
  urgent: 3,
  attention: 2,
  next: 1,
  steady: 0,
};

const DEFAULT_ANALYTICS_RANGE_BY_ZONE = {
  'heart-work': '30d',
  stewardship: '90d',
};

const DEFAULT_EMAIL_AUDIENCE_BY_ZONE = {
  communication: 'prospects',
  'heart-work': 'members_all',
  'guide-partners': 'guest_guides_active',
  'advisory-partners': 'advisory_partners',
  'support-partners': 'admins',
};

const createEmailContentState = (zoneId) => ({
  draftId: null,
  templateId: null,
  templateName: '',
  subject: '',
  body: '',
  heroImageUrl: '',
  audience: DEFAULT_EMAIL_AUDIENCE_BY_ZONE[zoneId] || 'everyone',
  singleEmailAddress: '',
  excludedEmails: '',
});

const getEmailDraftErrorMessage = (detail) => {
  if (!detail) return 'Failed to generate email draft';

  if (detail.includes('OPENAI_API_KEY')) {
    return 'Email drafting is not configured on the backend. Add OPENAI_API_KEY to the backend environment, restart or redeploy the backend, then rerun Monitoring.';
  }

  if (detail.includes('OpenAI support is not available')) {
    return 'Email drafting is not available on this deployment yet. Install the backend OpenAI dependency and redeploy.';
  }

  return detail;
};

const parseCustomRecipientEmails = (value) => {
  const seen = new Set();
  return String(value || '')
    .split(/[\s,;]+/)
    .map((email) => email.trim())
    .filter((email) => {
      if (!email) return false;
      const normalized = email.toLowerCase();
      if (seen.has(normalized)) return false;
      seen.add(normalized);
      return true;
    });
};

const pluralize = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const buildVisibleZoneContext = (zone, roomContextsById) => {
  const contexts = (zone?.roomIds || [])
    .map((roomId) => roomContextsById[roomId])
    .filter(Boolean);

  if (!contexts.length) {
    return {
      statusTone: 'steady',
      statusLabel: 'Ready',
    };
  }

  return contexts.reduce((selected, candidate) => {
    const selectedScore = ZONE_STATUS_PRIORITY[selected?.statusTone] ?? -1;
    const candidateScore = ZONE_STATUS_PRIORITY[candidate?.statusTone] ?? -1;
    return candidateScore > selectedScore ? candidate : selected;
  }, contexts[0]);
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, session, isAdmin, isAuthenticated, authLoading } = useAuth();
  const [activeTab, setActiveTabState] = useState(COMMAND_CENTER_HOME_TAB);
  const [activeRoomId, setActiveRoomId] = useState(DEFAULT_ROOM_ID);
  const [overviewStats, setOverviewStats] = useState(null);
  const [overviewActivity, setOverviewActivity] = useState([]);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overviewNotAuthorized, setOverviewNotAuthorized] = useState(false);
  const [overviewFounding40Status, setOverviewFounding40Status] = useState(null);
  const [analyticsTimeRange, setAnalyticsTimeRange] = useState('30d');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsAtRiskMembers, setAnalyticsAtRiskMembers] = useState([]);
  const [analyticsOnboarding, setAnalyticsOnboarding] = useState(null);
  const [analyticsWeeklyRhythm, setAnalyticsWeeklyRhythm] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [feedbackAnalyzing, setFeedbackAnalyzing] = useState(false);
  const [feedbackAiInsights, setFeedbackAiInsights] = useState(null);
  const [feedbackItems, setFeedbackItems] = useState([]);
  const [feedbackLoading, setFeedbackLoading] = useState(true);
  const [guestPassEmail, setGuestPassEmail] = useState("");
  const [guestPassName, setGuestPassName] = useState("");
  const [guestPassSending, setGuestPassSending] = useState(false);
  const [sentGuestPasses, setSentGuestPasses] = useState([]);
  const [guestPassesLoading, setGuestPassesLoading] = useState(true);
  const [guideSummary, setGuideSummary] = useState(null);
  const [activeToolContext, setActiveToolContext] = useState(null);
  const [emailContent, setEmailContentState] = useState(createEmailContentState(DEFAULT_ROOM_ID));
  const [emailDraftAssist, setEmailDraftAssist] = useState({
    goal: '',
    details: '',
  });
  const [emailSending, setEmailSending] = useState(false);
  const [emailTestSending, setEmailTestSending] = useState(false);
  const [emailScheduling, setEmailScheduling] = useState(false);
  const [emailScheduleAt, setEmailScheduleAt] = useState('');
  const [emailHeroUploading, setEmailHeroUploading] = useState(false);
  const [emailPreview, setEmailPreview] = useState(null);
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
  const [emailPreviewLoading, setEmailPreviewLoading] = useState(false);
  const [emailDraftGenerating, setEmailDraftGenerating] = useState(false);
  const [savedEmailDrafts, setSavedEmailDrafts] = useState([]);
  const [savedEmailDraftsLoading, setSavedEmailDraftsLoading] = useState(false);
  const [emailDraftSaving, setEmailDraftSaving] = useState(false);
  const [savedEmailTemplates, setSavedEmailTemplates] = useState([]);
  const [savedEmailTemplatesLoading, setSavedEmailTemplatesLoading] = useState(false);
  const [emailTemplateSaving, setEmailTemplateSaving] = useState(false);
  const [scheduledEmails, setScheduledEmails] = useState([]);
  const [scheduledEmailsLoading, setScheduledEmailsLoading] = useState(false);
  const [recentEmails, setRecentEmails] = useState([]);
  const [emailLoading, setEmailLoading] = useState(true);
  const [emailAnalytics, setEmailAnalytics] = useState(null);
  const [emailAnalyticsLoading, setEmailAnalyticsLoading] = useState(true);
  const [emailDeliveryIssues, setEmailDeliveryIssues] = useState([]);
  const [emailDeliveryIssueCounts, setEmailDeliveryIssueCounts] = useState(null);
  const [emailDeliveryIssuesLoading, setEmailDeliveryIssuesLoading] = useState(true);
  const [emailDeliveryIssuesImporting, setEmailDeliveryIssuesImporting] = useState(false);
  const [emailDeliveryIssuesLastImportedAt, setEmailDeliveryIssuesLastImportedAt] = useState(null);
  const [featurePlanningItems, setFeaturePlanningItems] = useState([]);
  const [featurePlanningLoading, setFeaturePlanningLoading] = useState(false);
  const [featurePlanningSaving, setFeaturePlanningSaving] = useState(false);
  const [featurePlanningEditing, setFeaturePlanningEditing] = useState(null);
  const [featurePlanningAnalysisLoadingId, setFeaturePlanningAnalysisLoadingId] = useState(null);
  const [pricingPrices, setPricingPrices] = useState({
    monthly: 47,
    annual: 397
  });
  const [pricingCoupons, setPricingCoupons] = useState([]);
  const [pricingShowCouponModal, setPricingShowCouponModal] = useState(false);
  const [pricingNewCoupon, setPricingNewCoupon] = useState({
    code: '',
    title: '',
    description: '',
    discount_type: 'percentage',
    discount_value: 10,
    max_uses: null
  });
  const [pricingLoading, setPricingLoading] = useState(true);
  const [pricingCreating, setPricingCreating] = useState(false);
  const [supportOverview, setSupportOverview] = useState(null);
  const [supportLoading, setSupportLoading] = useState(true);
  const [supportResyncingIds, setSupportResyncingIds] = useState([]);
  const [advisoryOverview, setAdvisoryOverview] = useState(null);
  const [, setAdvisoryLoading] = useState(true);
  const [superFanOverview, setSuperFanOverview] = useState(null);
  const [, setSuperFanLoading] = useState(true);
  const [marketingQueueDraft, setMarketingQueueDraft] = useState(null);
  const [newResource, setNewResource] = useState(EMPTY_RESOURCE_FORM);
  const [addingResource, setAddingResource] = useState(false);
  const [analyzingResources, setAnalyzingResources] = useState(false);
  const [resourceInsights, setResourceInsights] = useState(null);
  const [resources, setResources] = useState([]);
  const [resourcesLoading, setResourcesLoading] = useState(true);
  const [editingResource, setEditingResource] = useState(null);
  const [resourceEditForm, setResourceEditForm] = useState(EMPTY_RESOURCE_EDIT_FORM);
  const [recordingsViewMode, setRecordingsViewMode] = useState('all');
  const [recordingsFocusFilter, setRecordingsFocusFilter] = useState('all');
  const [analyzingRecordings, setAnalyzingRecordings] = useState(false);
  const [recordingInsights, setRecordingInsights] = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [recordingsLoading, setRecordingsLoading] = useState(true);
  const [uploadingRecording, setUploadingRecording] = useState(false);
  const [replacingRecordingMediaId, setReplacingRecordingMediaId] = useState(null);
  const [recordingUploadProgress, setRecordingUploadProgress] = useState(0);
  const [showRecordingUploadModal, setShowRecordingUploadModal] = useState(false);
  const [recordingUploadForm, setRecordingUploadForm] = useState({
    title: '',
    date: new Date().toISOString().split('T')[0],
    focus_area: 'marriage',
    duration: '',
    description: '',
    guest_guide_name: '',
    transcript: ''
  });
  const [selectedRecordingFile, setSelectedRecordingFile] = useState(null);
  const recordingFileInputRef = useRef(null);
  const [documents, setDocuments] = useState([]);
  const [systemHealthOverview, setSystemHealthOverview] = useState(null);
  const [systemHealthHistoryHours, setSystemHealthHistoryHours] = useState(24);
  const [systemHealthLoading, setSystemHealthLoading] = useState(true);
  const [systemHealthRunningProductionChecks, setSystemHealthRunningProductionChecks] = useState(false);
  const [systemHealthRunningLocalChecks, setSystemHealthRunningLocalChecks] = useState(false);
  const [documentCategories, setDocumentCategories] = useState([]);
  const [documentAvailableTags, setDocumentAvailableTags] = useState(['onboarding', 'guest_guide', 'member_resources', 'public', 'internal']);
  const [documentCategoryFilter, setDocumentCategoryFilter] = useState('all');
  const [documentsLoading, setDocumentsLoading] = useState(true);
  const [showDocumentAddModal, setShowDocumentAddModal] = useState(false);
  const [showDocumentEditModal, setShowDocumentEditModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [savingDocument, setSavingDocument] = useState(false);
  const [documentSearchTerm, setDocumentSearchTerm] = useState('');
  const [selectedDocumentFile, setSelectedDocumentFile] = useState(null);
  const [documentDragOver, setDocumentDragOver] = useState(false);
  const [documentNewTag, setDocumentNewTag] = useState('');
  const [documentUploadProgress, setDocumentUploadProgress] = useState(0);
  const documentFileInputRef = useRef(null);
  const [documentFormData, setDocumentFormData] = useState({
    name: '',
    description: '',
    category: 'general',
    access_level: 'members',
    is_public: false,
    tags: []
  });
  const [promptGenerating, setPromptGenerating] = useState(false);
  const [generatedPrompts, setGeneratedPrompts] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [selectedPromptRecording, setSelectedPromptRecording] = useState('');
  const [promptRecordings, setPromptRecordings] = useState([]);
  const [promptHistory, setPromptHistory] = useState([]);
  const [promptsLoading, setPromptsLoading] = useState(true);
  const [analyzingPrompts, setAnalyzingPrompts] = useState(false);
  const [promptInsights, setPromptInsights] = useState(null);
  const roomRailRef = useRef(null);
  const landingContentRef = useRef(null);
  const activeRoomSectionRef = useRef(null);
  const activeRoomHeaderRef = useRef(null);
  const toolContentRef = useRef(null);
  const pendingScrollTargetRef = useRef(null);
  const analyticsRangeTouchedByZoneRef = useRef({});
  const emailAudienceTouchedByZoneRef = useRef({});
  const accessToken = session?.access_token || "";
  const hasToken = accessToken || readTokenFromStorage();
  const adminFetch = useAdminFetch();
  const membersState = useMembersAdminState({ adminFetch });
  const siteSettingsState = useSiteSettingsAdminState({
    activeTab,
    adminFetch,
    hasToken,
  });
  const commandCenterQueueState = useCommandCenterQueueState({
    activeTab,
    adminFetch,
    hasToken,
  });
  const { siteSettings, handleTabRequest, siteSettingsTabProps } = siteSettingsState;
  const {
    queueState,
    queueLoading,
    queueRefreshing,
    queueError,
    creatingQueueItem,
    mutatingIds: commandCenterQueueMutatingIds,
    refreshQueue,
    createQueueItem,
    resolveQueueItem,
    deferQueueItem,
    reviewQueueItem,
    dismissQueueItem,
    updateQueueItem,
    deleteQueueItem,
  } = commandCenterQueueState;
  const { members, fetchMembers } = membersState;
  const pendingFocusReviewCount = recordings.filter((recording) => recording.status === 'draft_ready').length;
  const guideAttention = useMemo(() => deriveGuidePartnersState(guideSummary), [guideSummary]);
  const guidePendingAttentionCount = guideAttention.counts.onboardingCompleted > 0
    ? guideAttention.counts.onboardingCompleted
    : guideAttention.counts.onboardingSentOverdue;
  const isCommandCenterView = activeTab === COMMAND_CENTER_HOME_TAB;
  const isToolFirstTab = activeTab === 'guestguides';
  const hasScopedMembersView = activeTab === 'members' && (
    Array.isArray(activeToolContext?.targetMemberIds) && activeToolContext.targetMemberIds.length > 0
  );
  const suppressDadPartnersLandingChrome = activeTab === 'members' && (
    activeRoomId === 'support-dads' || activeRoomId === 'reach-dads'
  );
  const activeRoom = getAdminRoomById(activeRoomId) || getPrimaryRoomForTab(activeTab) || ADMIN_ROOMS[0];
  const activeZone = getAdminZoneForRoomOrTab(activeRoom?.id, activeTab) || ADMIN_ZONES[0];
  const activeZoneId = activeZone?.id || 'default';
  const [activeRoomHeaderHeight, setActiveRoomHeaderHeight] = useState(0);
  const activeTool = useMemo(
    () => activeZone?.tools?.find((tool) => tool.tab === activeTab && (!tool.roomId || tool.roomId === activeRoom?.id))
      || activeZone?.tools?.find((tool) => tool.tab === activeTab)
      || null,
    [activeRoom?.id, activeTab, activeZone]
  );
  const roomInsightSource = useMemo(() => ({
    stats: overviewStats,
    founding40Status: overviewFounding40Status,
    feedbackItems,
    feedbackAiInsights,
    analytics: analyticsData,
    atRiskMembers: analyticsAtRiskMembers,
    members,
    sentGuestPasses,
    guideSummary,
    emailDeliveryIssues,
    recordings,
    resources,
    resourceInsights,
    pricingCoupons,
    documents,
    siteSettings,
    supportOverview,
    advisoryOverview,
    superFanOverview,
  }), [
    analyticsAtRiskMembers,
    analyticsData,
    documents,
    emailDeliveryIssues,
    feedbackAiInsights,
    feedbackItems,
    guideSummary,
    members,
    overviewFounding40Status,
    overviewStats,
    recordings,
    pricingCoupons,
    resourceInsights,
    resources,
    sentGuestPasses,
    siteSettings,
    supportOverview,
    advisoryOverview,
    superFanOverview,
  ]);
  const roomContextsById = useMemo(
    () => Object.fromEntries(ADMIN_ROOMS.map((room) => [room.id, buildActiveRoomContext(room, roomInsightSource)])),
    [roomInsightSource]
  );
  const activeZoneContext = useMemo(
    () => buildActiveZoneContext(activeZone, roomContextsById, activeRoom?.id) || buildActiveRoomContext(activeRoom, roomInsightSource),
    [activeRoom, activeZone, roomContextsById, roomInsightSource]
  );
  const zoneContextsById = useMemo(
    () => Object.fromEntries(ADMIN_ZONES.map((zone) => [zone.id, buildVisibleZoneContext(zone, roomContextsById)])),
    [roomContextsById]
  );

  const scrollToSection = useCallback((ref) => {
    if (typeof window === 'undefined' || !ref?.current) return;

    const mainHeaderHeight = 64;
    const roomRailHeight = roomRailRef.current?.offsetHeight || 0;
    const offset = mainHeaderHeight + roomRailHeight + 20;
    const targetTop = ref.current.getBoundingClientRect().top + window.scrollY - offset;

    window.scrollTo({
      top: Math.max(0, targetTop),
      behavior: 'smooth',
    });
  }, []);

  useEffect(() => {
    const pendingTarget = pendingScrollTargetRef.current;
    if (!pendingTarget) return;

    const targetRef = pendingTarget === 'landing'
      ? landingContentRef
      : pendingTarget === 'room'
        ? activeRoomSectionRef
        : toolContentRef;

    const frame = window.requestAnimationFrame(() => {
      scrollToSection(targetRef);
      pendingScrollTargetRef.current = null;
    });

    return () => window.cancelAnimationFrame(frame);
  }, [activeRoomId, activeTab, scrollToSection]);

  useEffect(() => {
    if (typeof window === 'undefined' || isCommandCenterView || !activeRoom || activeTab === 'monitoring' || hasScopedMembersView) {
      setActiveRoomHeaderHeight(0);
      return undefined;
    }

    const measure = () => {
      const nextHeight = activeRoomHeaderRef.current?.offsetHeight || 0;
      setActiveRoomHeaderHeight((current) => (current === nextHeight ? current : nextHeight));
    };

    const frame = window.requestAnimationFrame(measure);
    window.addEventListener('resize', measure);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', measure);
    };
  }, [
    activeRoom,
    activeRoom?.id,
    activeTab,
    activeZone.tools.length,
    guidePendingAttentionCount,
    isCommandCenterView,
    activeRoom.question,
    activeRoom.supportingLine,
    activeRoom.title,
    activeTool?.label,
    activeZone.question,
    activeZone.supportingLine,
    activeZone.title,
    hasScopedMembersView,
    pendingFocusReviewCount,
    isToolFirstTab,
  ]);

  const setActiveTab = useCallback((nextTab, roomId, toolContext = null) => {
    const resolvedRoom = roomId
      ? getAdminRoomById(roomId)
      : getPrimaryRoomForTab(nextTab) || activeRoom;
    const resolvedRoomId = resolvedRoom?.id || null;
    const isSameTarget = nextTab === activeTab && resolvedRoomId === activeRoom?.id;
    const preservedMembersScope = nextTab === 'members' && !toolContext && (
      Array.isArray(activeToolContext?.targetMemberIds) && activeToolContext.targetMemberIds.length > 0
    )
      ? activeToolContext
      : null;
    const effectiveToolContext = toolContext || preservedMembersScope || null;
    const shouldOpenToolContent = nextTab === 'members' && (
      Array.isArray(effectiveToolContext?.targetMemberIds) && effectiveToolContext.targetMemberIds.length > 0
    );
    const nextScrollTarget = nextTab === COMMAND_CENTER_HOME_TAB
      ? 'landing'
      : shouldOpenToolContent
        ? null
        : nextTab === 'monitoring' || nextTab === 'guestguides'
          ? 'tool'
          : 'room';

    setActiveToolContext(effectiveToolContext ? { ...effectiveToolContext, requestKey: Date.now() } : null);
    handleTabRequest(nextTab);
    setActiveTabState(nextTab);
    pendingScrollTargetRef.current = nextScrollTarget;

    if (isSameTarget && nextScrollTarget) {
      const targetRef = nextScrollTarget === 'landing'
        ? landingContentRef
        : nextScrollTarget === 'room'
          ? activeRoomSectionRef
          : toolContentRef;
      window.requestAnimationFrame(() => {
        scrollToSection(targetRef);
        pendingScrollTargetRef.current = null;
      });
    }

    if (nextTab === COMMAND_CENTER_HOME_TAB) {
      return;
    }

    if (resolvedRoom?.id) {
      setActiveRoomId(resolvedRoom.id);
    }
  }, [activeRoom, activeTab, activeToolContext, handleTabRequest, scrollToSection]);

  const setEmailContent = useCallback((nextContent) => {
    setEmailContentState((prev) => {
      const resolved = typeof nextContent === 'function' ? nextContent(prev) : nextContent;
      if (resolved?.audience !== prev.audience) {
        emailAudienceTouchedByZoneRef.current[activeZoneId] = true;
      }
      return resolved;
    });
  }, [activeZoneId]);

  const handleMarketingQueuePrefill = useCallback((title) => {
    setMarketingQueueDraft({
      title,
      type: 'youtube',
      plannedDate: '',
      requestKey: Date.now(),
    });
    setActiveTab('marketingcontent', 'marketing');
  }, [setActiveTab]);

  const clearMarketingQueueDraft = useCallback(() => {
    setMarketingQueueDraft(null);
  }, []);

  const handleAnalyticsTimeRangeChange = useCallback((nextRange) => {
    analyticsRangeTouchedByZoneRef.current[activeZoneId] = true;
    setAnalyticsTimeRange(nextRange);
  }, [activeZoneId]);

  const openZone = useCallback((zoneId) => {
    const zone = getAdminZoneById(zoneId);
    if (!zone) return;
    setActiveToolContext(null);
    setActiveRoomId(zone.primaryRoomId);
    setActiveTabState(zone.primaryTab);
    pendingScrollTargetRef.current = zone.primaryTab === 'monitoring' ? 'tool' : 'room';
  }, []);

  const openCommandCenter = useCallback(() => {
    setActiveToolContext(null);
    setActiveTabState(COMMAND_CENTER_HOME_TAB);
    pendingScrollTargetRef.current = 'landing';
  }, []);

  useEffect(() => {
    if (activeTab !== 'analytics') return;
    const nextRange = DEFAULT_ANALYTICS_RANGE_BY_ZONE[activeZoneId];
    if (!nextRange || analyticsRangeTouchedByZoneRef.current[activeZoneId]) {
      return;
    }
    if (analyticsTimeRange !== nextRange) {
      setAnalyticsTimeRange(nextRange);
    }
  }, [activeTab, activeZoneId, analyticsTimeRange]);

  useEffect(() => {
    if (activeTab !== 'email') return;
    const nextAudience = DEFAULT_EMAIL_AUDIENCE_BY_ZONE[activeZoneId];
    if (!nextAudience || emailAudienceTouchedByZoneRef.current[activeZoneId]) {
      return;
    }
    if (emailContent.subject || emailContent.body) {
      return;
    }
    if (emailContent.audience !== nextAudience) {
      setEmailContentState((prev) => ({ ...prev, audience: nextAudience }));
    }
  }, [activeTab, activeZoneId, emailContent.audience, emailContent.body, emailContent.subject]);

  const fetchOverviewData = useCallback(async () => {
    if (activeTab !== COMMAND_CENTER_HOME_TAB) return;
    if (authLoading) return;

    if (!isAdmin || !accessToken) {
      setOverviewNotAuthorized(true);
      setOverviewLoading(false);
      return;
    }

    setOverviewLoading(true);
    setOverviewNotAuthorized(false);

    try {
      const [statsRes, activityRes, founding40Res] = await Promise.all([
        adminFetch('/admin/stats'),
        adminFetch('/admin/analytics/activity?limit=500'),
        adminFetch('/admin/founding40/status'),
      ]);

      if ([statsRes, activityRes, founding40Res].some((res) => res.status === 401 || res.status === 403)) {
        setOverviewNotAuthorized(true);
        return;
      }

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setOverviewStats(statsData);
      }

      if (activityRes.ok) {
        const activityData = await activityRes.json();
        setOverviewActivity(Array.isArray(activityData) ? activityData : (activityData?.items || []));
      }

      if (founding40Res.ok) {
        const founding40Data = await founding40Res.json();
        setOverviewFounding40Status(founding40Data);
      }
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    } finally {
      setOverviewLoading(false);
    }
  }, [accessToken, activeTab, adminFetch, authLoading, isAdmin]);

  const fetchAnalytics = useCallback(async () => {
    if (activeTab !== 'analytics' && activeTab !== COMMAND_CENTER_HOME_TAB) return;

    setAnalyticsLoading(true);
    try {
      const [analyticsRes, onboardingRes, weeklyRhythmRes] = await Promise.all([
        adminFetch(`/admin/analytics?time_range=${analyticsTimeRange}`),
        adminFetch('/admin/analytics/onboarding?stalled_days=7&limit=12'),
        adminFetch(`/admin/analytics/weekly-rhythm?time_range=${analyticsTimeRange}&limit=8`),
      ]);

      if (analyticsRes.ok) {
        const data = await analyticsRes.json();
        setAnalyticsData(data);
        const candidates = data?.at_risk_members || data?.retention?.at_risk_members || [];
        setAnalyticsAtRiskMembers(Array.isArray(candidates) ? candidates.slice(0, 5) : []);
      }

      if (onboardingRes.ok) {
        const data = await onboardingRes.json();
        setAnalyticsOnboarding(data);
      } else {
        setAnalyticsOnboarding(null);
      }

      if (weeklyRhythmRes.ok) {
        const data = await weeklyRhythmRes.json();
        setAnalyticsWeeklyRhythm(data);
      } else {
        setAnalyticsWeeklyRhythm(null);
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      setAnalyticsOnboarding(null);
      setAnalyticsWeeklyRhythm(null);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [activeTab, adminFetch, analyticsTimeRange]);

  const fetchFeedback = useCallback(async () => {
    if (activeTab !== 'feedback' && activeTab !== COMMAND_CENTER_HOME_TAB) return;

    setFeedbackLoading(true);
    try {
      const response = await adminFetch('/admin/feedback');
      if (response.ok) {
        const data = await response.json();
        setFeedbackItems(data);
      }
    } catch (error) {
      console.error('Failed to fetch feedback:', error);
    } finally {
      setFeedbackLoading(false);
    }
  }, [activeTab, adminFetch]);

  const runFeedbackAIAnalysis = useCallback(async () => {
    setFeedbackAnalyzing(true);
    try {
      const response = await adminFetch('/admin/feedback/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });

      if (!response.ok) {
        throw new Error('Failed to analyze feedback');
      }

      const data = await response.json();
      setFeedbackAiInsights(data);
    } catch (error) {
      console.error(error);
    } finally {
      setFeedbackAnalyzing(false);
    }
  }, [adminFetch]);

  const fetchGuestPasses = useCallback(async () => {
    setGuestPassesLoading(true);
    try {
      const response = await adminFetch("/admin/guest-passes");
      if (response.ok) {
        const data = await response.json();
        const arr = Array.isArray(data) ? data : [];
        arr.sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
        setSentGuestPasses(arr);
      } else {
        const err = await response.json().catch(() => ({}));
        console.error("Fetch guest passes failed:", err);
        setSentGuestPasses([]);
      }
    } catch (error) {
      console.error("Failed to fetch guest passes:", error);
      setSentGuestPasses([]);
    } finally {
      setGuestPassesLoading(false);
    }
  }, [adminFetch]);

  const fetchGuideSummary = useCallback(async () => {
    try {
      const [summaryResponse, campaignsResponse] = await Promise.all([
        adminFetch('/admin/guest-guides?status=all&sort_by=updated_at&sort_order=desc'),
        adminFetch('/admin/guest-guides/campaigns/active'),
      ]);

      if (!summaryResponse.ok) return;

      const data = await summaryResponse.json();
      const campaignsData = campaignsResponse.ok ? await campaignsResponse.json() : { campaigns: [] };
      setGuideSummary({
        total: data?.total || 0,
        statusCounts: data?.status_counts || {},
        inviteStageCounts: data?.invite_stage_counts || {},
        guides: data?.guides || [],
        activeCampaigns: campaignsData?.campaigns || [],
      });
    } catch (error) {
      console.error('Failed to fetch guide summary:', error);
    }
  }, [adminFetch]);

  const handleSendGuestPass = useCallback(async (e) => {
    e.preventDefault();

    const recipient_email = (guestPassEmail || "").trim();
    const recipient_name =
      (guestPassName || "").trim() || (recipient_email ? recipient_email.split("@")[0] : "");

    if (!recipient_email) {
      toast.error("Please enter an email address");
      return;
    }

    setGuestPassSending(true);
    try {
      // IMPORTANT: use apiFetch so Authorization: Bearer ... is included
      const response = await adminFetch("/admin/guest-passes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ recipient_email, recipient_name }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        toast.error(err?.detail || "Failed to send invitation");
        return;
      }

      const result = await response.json();
      toast.success(result?.message || "Invitation sent");
      setGuestPassEmail("");
      setGuestPassName("");
      await fetchGuestPasses();
    } catch (error) {
      console.error("Error sending invitation:", error);
      toast.error("Failed to send invitation");
    } finally {
      setGuestPassSending(false);
    }
  }, [adminFetch, fetchGuestPasses, guestPassEmail, guestPassName]);

  const isGuestPassExpired = (expires_at) => {
    if (!expires_at) return false;
    const exp = new Date(expires_at);
    return !isNaN(exp.getTime()) && exp.getTime() < Date.now();
  };

  const copyGuestPassCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success("Code copied to clipboard!");
    } catch (err) {
      const textArea = document.createElement("textarea");
      textArea.value = code;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        toast.success("Code copied to clipboard!");
      } catch (e) {
        toast.error("Could not copy code. Please copy manually: " + code);
      }
      document.body.removeChild(textArea);
    }
  };

  const fetchEmailHistory = useCallback(async () => {
    if (activeTab !== 'email') return;

    setEmailLoading(true);
    try {
      const response = await adminFetch('/admin/email/history?limit=10');
      if (response.ok) {
        const data = await response.json();
        setRecentEmails(data || []);
      }
    } catch (error) {
      console.error('Failed to fetch email history:', error);
    } finally {
      setEmailLoading(false);
    }
  }, [activeTab, adminFetch]);

  const fetchEmailAnalytics = useCallback(async () => {
    if (activeTab !== 'email') return;

    setEmailAnalyticsLoading(true);
    try {
      const response = await adminFetch('/admin/email/analytics?days=30');
      if (response.ok) {
        const data = await response.json();
        setEmailAnalytics(data && typeof data === 'object' ? data : null);
      }
    } catch (error) {
      console.error('Failed to fetch email analytics:', error);
    } finally {
      setEmailAnalyticsLoading(false);
    }
  }, [activeTab, adminFetch]);

  const fetchSavedEmailDrafts = useCallback(async () => {
    if (activeTab !== 'email') return;

    setSavedEmailDraftsLoading(true);
    try {
      const query = new URLSearchParams({ zone_id: activeZoneId, limit: '25' });
      const response = await adminFetch(`/admin/email/drafts?${query.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setSavedEmailDrafts(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Failed to fetch saved email drafts:', error);
    } finally {
      setSavedEmailDraftsLoading(false);
    }
  }, [activeTab, activeZoneId, adminFetch]);

  const fetchSavedEmailTemplates = useCallback(async () => {
    if (activeTab !== 'email') return;

    setSavedEmailTemplatesLoading(true);
    try {
      const query = new URLSearchParams({ zone_id: activeZoneId, limit: '25' });
      const response = await adminFetch(`/admin/email/templates?${query.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setSavedEmailTemplates(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Failed to fetch saved email templates:', error);
    } finally {
      setSavedEmailTemplatesLoading(false);
    }
  }, [activeTab, activeZoneId, adminFetch]);

  const fetchScheduledEmails = useCallback(async () => {
    if (activeTab !== 'email') return;

    setScheduledEmailsLoading(true);
    try {
      const query = new URLSearchParams({ zone_id: activeZoneId, limit: '25' });
      const response = await adminFetch(`/admin/email/scheduled?${query.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setScheduledEmails(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Failed to fetch scheduled emails:', error);
    } finally {
      setScheduledEmailsLoading(false);
    }
  }, [activeTab, activeZoneId, adminFetch]);

  const fetchEmailDeliveryIssues = useCallback(async () => {
    if (
      activeTab !== 'email' &&
      activeTab !== 'guestguides' &&
      activeTab !== COMMAND_CENTER_HOME_TAB &&
      activeRoom.id !== 'guide-partners' &&
      activeRoom.id !== 'reach-dads' &&
      activeRoom.id !== 'support-dads'
    ) return;

    setEmailDeliveryIssuesLoading(true);
    try {
      const response = await adminFetch('/admin/email/delivery-issues');
      if (response.ok) {
        const data = await response.json();
        setEmailDeliveryIssues(Array.isArray(data?.issues) ? data.issues : []);
        setEmailDeliveryIssueCounts(data?.counts || null);
        setEmailDeliveryIssuesLastImportedAt(data?.last_updated_at || data?.last_imported_at || null);
      }
    } catch (error) {
      console.error('Failed to fetch email delivery issues:', error);
    } finally {
      setEmailDeliveryIssuesLoading(false);
    }
  }, [activeRoom.id, activeTab, adminFetch]);

  const fetchFeaturePlanning = useCallback(async () => {
    if (activeTab !== 'featureplanning') return;

    setFeaturePlanningLoading(true);
    try {
      const response = await adminFetch('/admin/feature-planning?limit=100');
      if (!response.ok) {
        throw new Error('Failed to load feature planning items');
      }
      const data = await response.json();
      setFeaturePlanningItems(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch feature planning:', error);
      toast.error('Failed to load feature planning items');
    } finally {
      setFeaturePlanningLoading(false);
    }
  }, [activeTab, adminFetch]);

  const handleSaveFeaturePlanning = useCallback(async (payload) => {
    const featureId = payload?.feature_id;
    const body = { ...payload };
    delete body.feature_id;
    setFeaturePlanningSaving(true);
    try {
      const response = await adminFetch(
        featureId ? `/admin/feature-planning/${encodeURIComponent(featureId)}` : '/admin/feature-planning',
        {
          method: featureId ? 'PATCH' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      );
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error(result?.detail || 'Failed to save planning item');
        return false;
      }
      toast.success(featureId ? 'Planning item updated' : 'Planning item added');
      await fetchFeaturePlanning();
      return true;
    } catch (error) {
      console.error('Failed to save feature planning item:', error);
      toast.error('Failed to save planning item');
      return false;
    } finally {
      setFeaturePlanningSaving(false);
    }
  }, [adminFetch, fetchFeaturePlanning]);

  const handleAnalyzeFeaturePlanning = useCallback(async (item) => {
    const featureId = item?.feature_id;
    if (!featureId) return;
    setFeaturePlanningAnalysisLoadingId(featureId);
    try {
      const response = await adminFetch(`/admin/feature-planning/${encodeURIComponent(featureId)}/analyze`, { method: 'POST' });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error(result?.detail || 'AI analysis failed');
        return;
      }
      toast.success('AI analysis saved');
      await fetchFeaturePlanning();
    } catch (error) {
      console.error('Failed to analyze feature planning item:', error);
      toast.error('AI analysis failed');
    } finally {
      setFeaturePlanningAnalysisLoadingId(null);
    }
  }, [adminFetch, fetchFeaturePlanning]);

  const handleImportEmailDeliveryIssues = useCallback(async (files) => {
    const selectedFiles = Array.from(files || []).filter(Boolean);
    if (!selectedFiles.length) {
      toast.error('Please choose at least one Resend CSV export.');
      return;
    }

    setEmailDeliveryIssuesImporting(true);
    try {
      const formData = new FormData();
      selectedFiles.forEach((file) => {
        formData.append('files', file);
      });

      const response = await adminFetch('/admin/email/delivery-issues/import', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        toast.error(error?.detail || 'Failed to import email delivery issues');
        return;
      }

      const result = await response.json();
      toast.success(result?.message || 'Email delivery issues imported');
      await fetchEmailDeliveryIssues();
    } catch (error) {
      console.error('Failed to import email delivery issues:', error);
      toast.error('Failed to import email delivery issues');
    } finally {
      setEmailDeliveryIssuesImporting(false);
    }
  }, [adminFetch, fetchEmailDeliveryIssues]);

  const buildEmailPayload = useCallback(() => {
    const customRecipientEmails = parseCustomRecipientEmails(emailContent.singleEmailAddress);
    const plainBody = htmlToPlainText(emailContent.body);
    const richBody = normalizeEmailBodyHtml(emailContent.body);
    if (!emailContent.subject || !plainBody) {
      toast.error('Please fill in subject and body');
      return null;
    }
    if (emailContent.audience === 'single_email' && customRecipientEmails.length === 0) {
      toast.error('Please enter at least one email address');
      return null;
    }
    if (emailContent.audience === 'single_email') {
      const blockingIssue = (emailDeliveryIssues || []).find((issue) => {
        const issueEmail = String(issue?.email || '').trim().toLowerCase();
        if (!issueEmail || !customRecipientEmails.some((email) => email.toLowerCase() === issueEmail)) return false;
        const category = String(issue?.latest_status_category || '').trim().toLowerCase();
        const status = String(issue?.latest_status || '').trim().toLowerCase();
        const exclude = issue?.exclude_from_automation;
        if (typeof exclude === 'boolean') return exclude;
        return status === 'suppressed' || status === 'complained' || category === 'hard_bounce' || status === 'bounced';
      });
      if (blockingIssue) {
        toast.error(
          `${blockingIssue?.latest_issue_label || 'Delivery issue'}: ${blockingIssue?.latest_issue_summary || 'This address should not be emailed automatically right now.'}`
        );
        return null;
      }
    }
    const payload = {
      subject: emailContent.subject,
      message: plainBody,
      message_html: richBody || null,
      hero_image_url: emailContent.heroImageUrl || null,
      recipient_type: emailContent.audience,
      excluded_emails: emailContent.audience === 'single_email'
        ? []
        : parseCustomRecipientEmails(emailContent.excludedEmails),
    };
    if (emailContent.audience === 'tier') {
      payload.tier = emailContent.tier ?? null;
    }
    if (emailContent.audience === 'single_email') {
      payload.recipient_type = 'custom';
      payload.recipient_emails = customRecipientEmails;
    }

    return payload;
  }, [emailContent, emailDeliveryIssues]);

  const buildSavedEmailDraftPayload = useCallback(() => {
    const customRecipientEmails = parseCustomRecipientEmails(emailContent.singleEmailAddress);
    return {
      draft_id: emailContent.draftId || null,
      zone_id: activeZoneId,
      subject: String(emailContent.subject || '').trim(),
      message: htmlToPlainText(emailContent.body),
      message_html: normalizeEmailBodyHtml(emailContent.body) || null,
      hero_image_url: emailContent.heroImageUrl || null,
      recipient_type: emailContent.audience || 'everyone',
      tier: emailContent.audience === 'tier' ? emailContent.tier ?? null : null,
      recipient_emails: emailContent.audience === 'single_email' ? customRecipientEmails : [],
    };
  }, [activeZoneId, emailContent]);

  const handleSaveEmailDraft = useCallback(async () => {
    setEmailDraftSaving(true);
    try {
      const response = await adminFetch('/admin/email/drafts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildSavedEmailDraftPayload()),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error(result?.detail || 'Failed to save email draft');
        return;
      }
      setEmailContentState((prev) => ({ ...prev, draftId: result.draft_id || prev.draftId }));
      toast.success('Email draft saved');
      await fetchSavedEmailDrafts();
    } catch (error) {
      console.error('Failed to save email draft:', error);
      toast.error('Failed to save email draft');
    } finally {
      setEmailDraftSaving(false);
    }
  }, [adminFetch, buildSavedEmailDraftPayload, fetchSavedEmailDrafts]);

  const handleLoadEmailDraft = useCallback(async (draftId) => {
    if (!draftId) return;
    try {
      const response = await adminFetch(`/admin/email/drafts/${encodeURIComponent(draftId)}`);
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error(result?.detail || 'Failed to load email draft');
        return;
      }
      setEmailContentState((prev) => ({
        ...prev,
        draftId: result.draft_id || draftId,
        subject: result.subject || '',
        body: result.message_html || normalizeEmailBodyHtml(result.message || ''),
        heroImageUrl: result.hero_image_url || '',
        audience: result.recipient_type === 'custom' ? 'single_email' : (result.recipient_type || 'everyone'),
        tier: result.tier || null,
        singleEmailAddress: Array.isArray(result.recipient_emails) ? result.recipient_emails.join('\n') : '',
      }));
      toast.success('Email draft loaded');
    } catch (error) {
      console.error('Failed to load email draft:', error);
      toast.error('Failed to load email draft');
    }
  }, [adminFetch]);

  const handleDeleteEmailDraft = useCallback(async (draftId) => {
    if (!draftId) return;
    try {
      const response = await adminFetch(`/admin/email/drafts/${encodeURIComponent(draftId)}`, { method: 'DELETE' });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error(result?.detail || 'Failed to delete email draft');
        return;
      }
      if (emailContent.draftId === draftId) {
        setEmailContentState((prev) => ({ ...prev, draftId: null }));
      }
      toast.success('Email draft deleted');
      await fetchSavedEmailDrafts();
    } catch (error) {
      console.error('Failed to delete email draft:', error);
      toast.error('Failed to delete email draft');
    }
  }, [adminFetch, emailContent.draftId, fetchSavedEmailDrafts]);

  const buildSavedEmailTemplatePayload = useCallback(() => ({
    template_id: emailContent.templateId || null,
    name: String(emailContent.templateName || '').trim(),
    zone_id: activeZoneId,
    subject: String(emailContent.subject || '').trim(),
    message: htmlToPlainText(emailContent.body),
    message_html: normalizeEmailBodyHtml(emailContent.body) || null,
    hero_image_url: emailContent.heroImageUrl || null,
    recipient_type: emailContent.audience === 'single_email' ? 'everyone' : (emailContent.audience || 'everyone'),
    tier: emailContent.audience === 'tier' ? emailContent.tier ?? null : null,
    recipient_emails: [],
  }), [activeZoneId, emailContent]);

  const handleSaveEmailTemplate = useCallback(async () => {
    const payload = buildSavedEmailTemplatePayload();
    if (!payload.name) {
      toast.error('Enter a template name first');
      return;
    }
    if (!payload.subject || !payload.message) {
      toast.error('Add a subject and body before saving a template');
      return;
    }
    if (emailContent.audience === 'single_email') {
      toast.error('Templates cannot save personal recipient addresses. Choose a reusable audience first.');
      return;
    }

    setEmailTemplateSaving(true);
    try {
      const response = await adminFetch('/admin/email/templates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error(result?.detail || 'Failed to save email template');
        return;
      }
      setEmailContentState((prev) => ({
        ...prev,
        templateId: result.template_id || prev.templateId,
        templateName: result.name || prev.templateName,
      }));
      toast.success('Email template saved');
      await fetchSavedEmailTemplates();
    } catch (error) {
      console.error('Failed to save email template:', error);
      toast.error('Failed to save email template');
    } finally {
      setEmailTemplateSaving(false);
    }
  }, [adminFetch, buildSavedEmailTemplatePayload, emailContent.audience, fetchSavedEmailTemplates]);

  const handleLoadEmailTemplate = useCallback(async (templateId) => {
    if (!templateId) return;
    try {
      const response = await adminFetch(`/admin/email/templates/${encodeURIComponent(templateId)}`);
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error(result?.detail || 'Failed to load email template');
        return;
      }
      setEmailContentState((prev) => ({
        ...prev,
        draftId: null,
        templateId: result.template_id || templateId,
        templateName: result.name || '',
        subject: result.subject || '',
        body: result.message_html || normalizeEmailBodyHtml(result.message || ''),
        heroImageUrl: result.hero_image_url || '',
        audience: result.recipient_type === 'custom' || result.recipient_type === 'single_email'
          ? 'everyone'
          : (result.recipient_type || 'everyone'),
        tier: result.tier || null,
        singleEmailAddress: '',
      }));
      toast.success('Email template loaded');
    } catch (error) {
      console.error('Failed to load email template:', error);
      toast.error('Failed to load email template');
    }
  }, [adminFetch]);

  const handleDeleteEmailTemplate = useCallback(async (templateId) => {
    if (!templateId) return;
    try {
      const response = await adminFetch(`/admin/email/templates/${encodeURIComponent(templateId)}`, { method: 'DELETE' });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error(result?.detail || 'Failed to delete email template');
        return;
      }
      if (emailContent.templateId === templateId) {
        setEmailContentState((prev) => ({ ...prev, templateId: null, templateName: '' }));
      }
      toast.success('Email template deleted');
      await fetchSavedEmailTemplates();
    } catch (error) {
      console.error('Failed to delete email template:', error);
      toast.error('Failed to delete email template');
    }
  }, [adminFetch, emailContent.templateId, fetchSavedEmailTemplates]);

  const handleUploadEmailHero = useCallback(async (file) => {
    if (!file) return;

    const supportedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!supportedTypes.includes(file.type)) {
      toast.error('Hero image must be a JPEG, PNG, or WebP file');
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      toast.error('Hero image must be under 3MB');
      return;
    }

    setEmailHeroUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const response = await adminFetch('/admin/email/hero-image', {
        method: 'POST',
        body: form,
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error(result?.detail || 'Failed to upload hero image');
        return;
      }

      setEmailContent((prev) => ({
        ...prev,
        heroImageUrl: result.hero_image_url || '',
      }));
      toast.success('Hero image uploaded');
    } catch (error) {
      toast.error('Failed to upload hero image');
    } finally {
      setEmailHeroUploading(false);
    }
  }, [adminFetch, setEmailContent]);

  const handlePreviewEmail = useCallback(async () => {
    const payload = buildEmailPayload();
    if (!payload) return;

    setEmailPreviewLoading(true);
    try {
      const response = await adminFetch('/admin/email/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json();
        toast.error(error.detail || 'Failed to load email preview');
        return;
      }

      const result = await response.json();
      setEmailPreview(result);
      setEmailPreviewOpen(true);
    } catch (error) {
      toast.error('Failed to load email preview');
    } finally {
      setEmailPreviewLoading(false);
    }
  }, [adminFetch, buildEmailPayload]);

  const handleSendTestEmail = useCallback(async () => {
    const plainBody = htmlToPlainText(emailContent.body);
    if (!emailContent.subject?.trim() || !plainBody) {
      toast.error('Please fill in subject and body before sending a test');
      return;
    }

    setEmailTestSending(true);
    try {
      const response = await adminFetch('/admin/email/test-send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: emailContent.subject.trim(),
          message: plainBody,
          message_html: normalizeEmailBodyHtml(emailContent.body) || null,
          hero_image_url: emailContent.heroImageUrl || null,
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error(result?.detail || 'Failed to send test email');
        return;
      }
      toast.success(result?.message || 'Test email sent to your admin address');
      await fetchEmailAnalytics();
    } catch (error) {
      console.error('Failed to send test email:', error);
      toast.error('Failed to send test email');
    } finally {
      setEmailTestSending(false);
    }
  }, [adminFetch, emailContent.body, emailContent.heroImageUrl, emailContent.subject, fetchEmailAnalytics]);

  const handleScheduleEmail = useCallback(async () => {
    if (!emailPreview || emailPreview.placeholder_warnings?.length || emailPreview.recipient_count < 1) {
      toast.error('Review a valid recipient preview before scheduling this email');
      return;
    }
    if (!emailScheduleAt) {
      toast.error('Choose a future date and time first');
      return;
    }

    const scheduledDate = new Date(emailScheduleAt);
    if (Number.isNaN(scheduledDate.getTime())) {
      toast.error('Choose a valid schedule date and time');
      return;
    }
    const payload = buildEmailPayload();
    if (!payload) return;
    payload.scheduled_for = scheduledDate.toISOString();
    payload.review_confirmed = true;

    setEmailScheduling(true);
    try {
      const response = await adminFetch('/admin/email/scheduled', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error(result?.detail || 'Failed to schedule email');
        return;
      }
      setEmailScheduleAt('');
      toast.success('Email scheduled');
      await fetchScheduledEmails();
      await fetchEmailAnalytics();
    } catch (error) {
      console.error('Failed to schedule email:', error);
      toast.error('Failed to schedule email');
    } finally {
      setEmailScheduling(false);
    }
  }, [adminFetch, buildEmailPayload, emailPreview, emailScheduleAt, fetchEmailAnalytics, fetchScheduledEmails]);

  const handleCancelScheduledEmail = useCallback(async (scheduleId) => {
    if (!scheduleId) return;
    try {
      const response = await adminFetch(`/admin/email/scheduled/${encodeURIComponent(scheduleId)}`, { method: 'DELETE' });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error(result?.detail || 'Failed to cancel scheduled email');
        return;
      }
      toast.success('Scheduled email cancelled');
      await fetchScheduledEmails();
    } catch (error) {
      console.error('Failed to cancel scheduled email:', error);
      toast.error('Failed to cancel scheduled email');
    }
  }, [adminFetch, fetchScheduledEmails]);

  const handleSendEmail = useCallback(async () => {
    const payload = buildEmailPayload();
    if (!payload) return;

    setEmailSending(true);
    try {

      const response = await adminFetch('/admin/email/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || 'Email sent!');
        emailAudienceTouchedByZoneRef.current[activeZoneId] = false;
        setEmailDraftAssist({ goal: '', details: '' });
        setEmailContentState(createEmailContentState(activeZoneId));
        setEmailPreview(null);
        setEmailPreviewOpen(false);
        await fetchEmailHistory();
        await fetchEmailAnalytics();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send email');
      }
    } catch (error) {
      toast.error('Failed to send email');
    } finally {
      setEmailSending(false);
    }
  }, [activeZoneId, adminFetch, buildEmailPayload, fetchEmailAnalytics, fetchEmailHistory]);

  const handleGenerateEmailDraft = useCallback(async () => {
    const hasPromptInput = Boolean(
      String(emailDraftAssist.goal || '').trim()
      || String(emailDraftAssist.details || '').trim()
      || String(emailContent.subject || '').trim()
      || String(htmlToPlainText(emailContent.body) || '').trim()
    );
    if (!hasPromptInput) {
      toast.error('Add the goal, details, or a rough draft before asking AI to help.');
      return;
    }

    setEmailDraftGenerating(true);
    try {
      const response = await adminFetch('/admin/email/draft', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          zone_id: activeZoneId,
          recipient_type: emailContent.audience === 'single_email' ? 'custom' : emailContent.audience,
          goal: emailDraftAssist.goal,
          details: emailDraftAssist.details,
          current_subject: emailContent.subject,
          current_body: htmlToPlainText(emailContent.body),
        }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error(getEmailDraftErrorMessage(payload?.detail));
        return;
      }

      setEmailContentState((prev) => ({
        ...prev,
        subject: payload.subject || prev.subject,
        body: payload.body ? normalizeEmailBodyHtml(payload.body) : prev.body,
      }));
      toast.success('Email draft generated');
    } catch (error) {
      console.error('Failed to generate email draft:', error);
      toast.error('Failed to generate email draft');
    } finally {
      setEmailDraftGenerating(false);
    }
  }, [activeZoneId, adminFetch, emailContent, emailDraftAssist]);

  const fetchCoupons = useCallback(async () => {
    try {
      const response = await adminFetch('/admin/coupons');
      if (response.ok) {
        const data = await response.json();
        setPricingCoupons(data);
      }
    } catch (error) {
      console.error('Failed to fetch coupons:', error);
    } finally {
      setPricingLoading(false);
    }
  }, [adminFetch]);

  const fetchSupportOverview = useCallback(async () => {
    setSupportLoading(true);
    try {
      const response = await adminFetch('/admin/support/overview');
      if (!response.ok) {
        throw new Error('Failed to load mission support overview');
      }
      const data = await response.json();
      setSupportOverview(data);
    } catch (error) {
      console.error('Failed to load mission support overview:', error);
      toast.error('Failed to load mission support overview');
    } finally {
      setSupportLoading(false);
    }
  }, [adminFetch]);

  const fetchAdvisoryOverview = useCallback(async () => {
    setAdvisoryLoading(true);
    try {
      const response = await adminFetch('/admin/advisory/partners');
      if (!response.ok) {
        throw new Error('Failed to load advisory partner overview');
      }
      const data = await response.json();
      setAdvisoryOverview(data);
    } catch (error) {
      console.error('Failed to load advisory partner overview:', error);
      toast.error('Failed to load advisory partner overview');
    } finally {
      setAdvisoryLoading(false);
    }
  }, [adminFetch]);

  const fetchSuperFanOverview = useCallback(async () => {
    setSuperFanLoading(true);
    try {
      const response = await adminFetch('/admin/superfan/accounts');
      if (!response.ok) {
        throw new Error('Failed to load super fan accounts');
      }
      const data = await response.json();
      setSuperFanOverview(data);
    } catch (error) {
      console.error('Failed to load super fan accounts:', error);
      toast.error('Failed to load super fan accounts');
    } finally {
      setSuperFanLoading(false);
    }
  }, [adminFetch]);

  const handleResyncSupporter = useCallback(async (supporterId) => {
    setSupportResyncingIds((prev) => [...prev, supporterId]);
    try {
      const response = await adminFetch(`/admin/support/${supporterId}/resync`, {
        method: 'POST',
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to resync supporter');
      }
      toast.success('Supporter resynced');
      await fetchSupportOverview();
    } catch (error) {
      console.error('Failed to resync supporter:', error);
      toast.error(error.message || 'Failed to resync supporter');
    } finally {
      setSupportResyncingIds((prev) => prev.filter((id) => id !== supporterId));
    }
  }, [adminFetch, fetchSupportOverview]);

  const handleCreateCoupon = useCallback(async () => {
    setPricingCreating(true);

    const payload = {
      code: (pricingNewCoupon.code || "").trim(),
      title: (pricingNewCoupon.title || "").trim(),
      description: (pricingNewCoupon.description || "").trim(),
      discount_type: pricingNewCoupon.discount_type,
      discount_value: Number(pricingNewCoupon.discount_value),
      max_uses:
        pricingNewCoupon.max_uses === null || pricingNewCoupon.max_uses === "" || typeof pricingNewCoupon.max_uses === "undefined"
          ? null
          : Number(pricingNewCoupon.max_uses),
    };

    if (!payload.code) {
      toast.error("Coupon code is required");
      setPricingCreating(false);
      return;
    }

    try {
      const response = await adminFetch("/admin/coupons", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast.success("Coupon created!");
        setPricingShowCouponModal(false);
        setPricingNewCoupon({
          code: "",
          title: "",
          description: "",
          discount_type: "percentage",
          discount_value: 10,
          max_uses: null,
        });

        try {
          await fetchCoupons();
        } catch (e) {
          console.error("fetchCoupons failed after create:", e);
          toast.error("Coupon created, but refresh failed. Reload the page.");
        }
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || "Failed to create coupon");
      }
    } catch (error) {
      console.error("Create coupon error:", error);
      toast.error("Failed to create coupon");
    } finally {
      setPricingCreating(false);
    }
  }, [adminFetch, pricingNewCoupon, fetchCoupons]);

  const handleDeleteCoupon = useCallback(async (couponId) => {
    try {
      const response = await adminFetch(`/admin/coupons/${couponId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        toast.error(err?.detail || 'Failed to delete coupon');
        return;
      }

      toast.success('Coupon deleted');

      try {
        await fetchCoupons();
      } catch (e) {
        console.error('fetchCoupons failed after delete:', e);
        toast.error('Deleted, but refresh failed. Reload the page.');
      }
    } catch (error) {
      console.error('Delete coupon error:', error);
      toast.error('Failed to delete coupon');
    }
  }, [adminFetch, fetchCoupons]);

  const fetchResources = useCallback(async () => {
    setResourcesLoading(true);
    try {
      const response = await adminFetch('/admin/resources');
      if (!response.ok) {
        throw new Error('Failed to load resources');
      }

      const data = await response.json();
      const grouped = data?.resources || {};
      const flatResources = Object.entries(grouped)
        .flatMap(([category, items]) =>
          (items || []).map((item) => ({
            ...item,
            category: item.category || category,
          }))
        )
        .filter(Boolean)
        .sort((a, b) => {
          const categoryCompare = (a.category || '').localeCompare(b.category || '');
          if (categoryCompare !== 0) return categoryCompare;
          return (a.name || '').localeCompare(b.name || '');
        });

      setResources(flatResources);
    } catch (error) {
      console.error('Failed to fetch resources:', error);
      toast.error('Failed to load resources');
    } finally {
      setResourcesLoading(false);
    }
  }, [adminFetch]);

  const handleAddResource = async (e) => {
    e.preventDefault();
    if (!newResource.category || !newResource.name) {
      toast.error('Please fill in required fields');
      return;
    }
    setAddingResource(true);
    try {
      const response = await adminFetch('/admin/resources', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category: newResource.category,
          name: newResource.name,
          url: newResource.url || null,
          author: newResource.author || null,
          description: newResource.description || null,
          media_type: newResource.mediaType || null,
          amazon_url: newResource.amazonUrl || null,
          recommendation_note: newResource.recommendationNote || null,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Failed to add resource');
      }

      setNewResource(EMPTY_RESOURCE_FORM);
      toast.success('Resource added');
      await fetchResources();
    } catch (error) {
      toast.error(error?.message || 'Failed to add resource');
    } finally {
      setAddingResource(false);
    }
  };

  const handleStartEditResource = (resource) => {
    setEditingResource(resource);
    setResourceEditForm({
      name: resource.name || '',
      url: resource.url || '',
      author: resource.author || '',
      description: resource.description || '',
      mediaType: resource.media_type || '',
      amazonUrl: resource.amazon_url || '',
      recommendationNote: resource.recommendation_note || ''
    });
  };

  const handleSaveResourceEdit = async () => {
    if (!editingResource) return;
    try {
      const response = await adminFetch(`/admin/resources/${editingResource.resource_id || editingResource.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: resourceEditForm.name,
          url: resourceEditForm.url || null,
          author: resourceEditForm.author || null,
          description: resourceEditForm.description || null,
          media_type: resourceEditForm.mediaType || null,
          amazon_url: resourceEditForm.amazonUrl || null,
          recommendation_note: resourceEditForm.recommendationNote || null,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Failed to update resource');
      }

      setEditingResource(null);
      setResourceEditForm(EMPTY_RESOURCE_EDIT_FORM);
      toast.success('Resource updated');
      await fetchResources();
    } catch (error) {
      toast.error(error?.message || 'Failed to update resource');
    }
  };

  const handleDeleteResource = async (resourceId) => {
    if (!confirm('Are you sure you want to delete this resource?')) return;
    try {
      const response = await adminFetch(`/admin/resources/${resourceId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Failed to delete resource');
      }

      toast.success('Resource deleted');
      await fetchResources();
    } catch (error) {
      toast.error(error?.message || 'Failed to delete resource');
    }
  };

  const runResourceAnalysis = async () => {
    setAnalyzingResources(true);
    await new Promise(r => setTimeout(r, 2000));
    setResourceInsights({
      topClicked: 'The BEMA Podcast (89 clicks) - Podcast category is most popular',
      recommendations: [
        'Add more podcasts - highest engagement category',
        'Consider "The Dad Edge Podcast" - similar audience',
        'Add "The Meaning of Marriage" by Tim Keller - frequently requested',
        'Include video series from RightNow Media'
      ],
      gaps: [
        'No resources for single dads',
        'Limited career/work content',
        'No Spanish language resources'
      ]
    });
    setAnalyzingResources(false);
  };

  const fetchRecordings = useCallback(async () => {
    setRecordingsLoading(true);
    try {
      const response = await adminFetch('/recordings');
      if (response.ok) {
        const data = await response.json();
        setRecordings(data || []);
      }
    } catch (error) {
      console.error('Failed to fetch recordings:', error);
    } finally {
      setRecordingsLoading(false);
    }
  }, [adminFetch]);

  const handleRecordingFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'audio/mpeg', 'audio/wav'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Invalid file type. Please upload MP4, WEBM, MOV, MP3, or WAV');
        return;
      }
      if (file.size > RECORDING_MAX_UPLOAD_BYTES) {
        toast.error('File too large. Max 2GB');
        return;
      }
      setSelectedRecordingFile(file);
      setShowRecordingUploadModal(true);
    }
  };

  const handleRecordingUpload = async () => {
    if (!selectedRecordingFile || !recordingUploadForm.title) {
      toast.error('Please select a file and enter a title');
      return;
    }

    setUploadingRecording(true);
    setRecordingUploadProgress(10);

    try {
      const formData = new FormData();
      formData.append('file', selectedRecordingFile);
      formData.append('title', recordingUploadForm.title);
      formData.append('date', recordingUploadForm.date);
      formData.append('focus_area', recordingUploadForm.focus_area);
      formData.append('duration', recordingUploadForm.duration);
      formData.append('description', recordingUploadForm.description);
      formData.append('guest_guide_name', recordingUploadForm.guest_guide_name);
      formData.append('transcript', recordingUploadForm.transcript);

      setRecordingUploadProgress(30);

      const response = await adminFetch('/recordings', {
        method: 'POST',
        body: formData,
        timeoutMs: RECORDING_UPLOAD_TIMEOUT_MS,
      });

      setRecordingUploadProgress(90);

      if (response.ok) {
        const uploadedRecording = await response.json();
        if (uploadedRecording?.recording_id) {
          await adminFetch(`/admin/analyze-recording/${uploadedRecording.recording_id}`, {
            method: 'POST'
          });
        }
        toast.success('Recording uploaded successfully!');
        setShowRecordingUploadModal(false);
        setSelectedRecordingFile(null);
        setRecordingUploadForm({
          title: '',
          date: new Date().toISOString().split('T')[0],
          focus_area: 'marriage',
          duration: '',
          description: '',
          guest_guide_name: '',
          transcript: ''
        });
        await fetchRecordings();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Upload failed');
      }
    } catch (error) {
      toast.error(error?.message || 'Upload failed');
    } finally {
      setUploadingRecording(false);
      setRecordingUploadProgress(0);
    }
  };

  const handleReplaceRecordingMedia = async (recordingId, file) => {
    if (!recordingId || !file) {
      toast.error('Please choose a replacement file');
      return;
    }

    const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'audio/mpeg', 'audio/wav'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload MP4, WEBM, MOV, MP3, or WAV');
      return;
    }
    if (file.size > RECORDING_MAX_UPLOAD_BYTES) {
      toast.error('File too large. Max 2GB');
      return;
    }

    setReplacingRecordingMediaId(recordingId);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await adminFetch(`/recordings/${recordingId}/media`, {
        method: 'PUT',
        body: formData,
        timeoutMs: RECORDING_UPLOAD_TIMEOUT_MS,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Failed to replace recording media');
      }

      toast.success('Recording media replaced');
      await fetchRecordings();
    } catch (error) {
      toast.error(error?.message || 'Failed to replace recording media');
      throw error;
    } finally {
      setReplacingRecordingMediaId(null);
    }
  };

  const runRecordingAnalysis = async () => {
    if (recordings.length === 0) {
      toast.error('No recordings to analyze');
      return;
    }
    setAnalyzingRecordings(true);
    try {
      const response = await adminFetch('/admin/recordings/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      if (response.ok) {
        const data = await response.json();
        setRecordingInsights(data);
        toast.success('Analysis complete!');
      } else {
        setRecordingInsights({
          topRated: 'Analysis based on ' + recordings.length + ' recordings',
          mostViewed: recordings[0]?.title || 'No recordings yet',
          suggestions: [
            'Upload more recordings to get better insights',
            'Encourage members to rate recordings',
            'Consider different focus areas for variety'
          ],
          patterns: []
        });
      }
    } catch (error) {
      toast.error('Analysis failed');
    } finally {
      setAnalyzingRecordings(false);
    }
  };

  const fetchDocuments = useCallback(async () => {
    try {
      const relative = documentCategoryFilter === 'all'
        ? '/admin/documents'
        : `/admin/documents?category=${documentCategoryFilter}`;
      const response = await adminFetch(relative);
      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents || []);
        setDocumentCategories(data.categories || []);
        if (data.tags) setDocumentAvailableTags(data.tags);
      }
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      setDocumentsLoading(false);
    }
  }, [adminFetch, documentCategoryFilter]);

  const fetchSystemHealthOverview = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        history_hours: String(systemHealthHistoryHours),
        incident_limit: '30',
      });
      const response = await adminFetch(`/admin/system-health?${params.toString()}`);
      if (!response.ok) {
        throw new Error(`system health fetch failed: ${response.status}`);
      }
      const data = await response.json();
      setSystemHealthOverview(data);
    } catch (error) {
      console.error('Failed to fetch system health overview:', error);
      toast.error('Failed to load monitoring data');
    } finally {
      setSystemHealthLoading(false);
    }
  }, [adminFetch, systemHealthHistoryHours]);

  const runProductionSystemHealthChecks = useCallback(async () => {
    setSystemHealthRunningProductionChecks(true);
    try {
      const response = await adminFetch('/admin/system-health/run-production-checks', {
        method: 'POST',
      });
      if (!response.ok) {
        throw new Error(`run checks failed: ${response.status}`);
      }
      toast.success('Production checks completed');
      setSystemHealthLoading(true);
      await fetchSystemHealthOverview();
    } catch (error) {
      console.error('Failed to run production system health checks:', error);
      toast.error('Failed to run production checks');
    } finally {
      setSystemHealthRunningProductionChecks(false);
    }
  }, [adminFetch, fetchSystemHealthOverview]);

  const runLocalSystemHealthChecks = useCallback(async () => {
    setSystemHealthRunningLocalChecks(true);
    try {
      const response = await adminFetch('/admin/system-health/run-local-checks', {
        method: 'POST',
      });
      if (!response.ok) {
        throw new Error(`run local checks failed: ${response.status}`);
      }
      toast.success('Local checks completed');
      setSystemHealthLoading(true);
      await fetchSystemHealthOverview();
    } catch (error) {
      console.error('Failed to run local system health checks:', error);
      toast.error('Failed to run local checks');
    } finally {
      setSystemHealthRunningLocalChecks(false);
    }
  }, [adminFetch, fetchSystemHealthOverview]);

  const resetDocumentForm = () => {
    setDocumentFormData({
      name: '',
      description: '',
      category: 'general',
      access_level: 'members',
      is_public: false,
      tags: []
    });
    setSelectedDocumentFile(null);
    setDocumentUploadProgress(0);
  };

  const validateAndSetDocumentFile = (file) => {
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];

    if (!allowedTypes.includes(file.type) && !file.name.endsWith('.pdf')) {
      toast.error('Only PDF and Word documents are allowed');
      return;
    }

    if (file.size > 25 * 1024 * 1024) {
      toast.error('File size must be under 25MB');
      return;
    }

    setSelectedDocumentFile(file);
    if (!documentFormData.name) {
      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
      setDocumentFormData(prev => ({ ...prev, name: nameWithoutExt }));
    }
  };

  const handleDocumentFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) validateAndSetDocumentFile(file);
  };

  const handleDocumentDrop = (e) => {
    e.preventDefault();
    setDocumentDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) validateAndSetDocumentFile(file);
  };

  const toggleDocumentTag = (tag) => {
    setDocumentFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag]
    }));
  };

  const buildDocumentUploadFormData = () => {
    const uploadData = new FormData();
    uploadData.append('file', selectedDocumentFile);
    uploadData.append('name', documentFormData.name);
    uploadData.append('description', documentFormData.description);
    uploadData.append('category', documentFormData.category);
    uploadData.append('access_level', documentFormData.access_level);
    uploadData.append('is_public', documentFormData.is_public);
    uploadData.append('tags', documentFormData.tags.join(','));
    return uploadData;
  };

  const addDocumentCustomTag = () => {
    const tag = documentNewTag.trim().toLowerCase().replace(/\s+/g, '_');
    if (tag && !documentAvailableTags.includes(tag)) {
      setDocumentAvailableTags(prev => [...prev, tag]);
      setDocumentFormData(prev => ({ ...prev, tags: [...prev.tags, tag] }));
      setDocumentNewTag('');
    }
  };

  const handleAddDocument = async () => {
    if (!documentFormData.name) {
      toast.error('Document name is required');
      return;
    }

    setSavingDocument(true);
    setDocumentUploadProgress(10);

    try {
      if (selectedDocumentFile) {
        const uploadData = buildDocumentUploadFormData();

        setDocumentUploadProgress(50);

        const response = await adminFetch(`/admin/documents/upload`, {
          method: 'POST',
          credentials: 'include',
          body: uploadData
        });

        setDocumentUploadProgress(90);

        if (response.ok) {
          toast.success('Document uploaded successfully');
          setShowDocumentAddModal(false);
          resetDocumentForm();
          fetchDocuments();
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Upload failed');
        }
      } else {
        const response = await adminFetch(`/admin/documents`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            ...documentFormData,
            content_type: 'text',
            content: documentFormData.content || 'No content',
          })
        });

        if (response.ok) {
          toast.success('Document created');
          setShowDocumentAddModal(false);
          resetDocumentForm();
          fetchDocuments();
        } else {
          toast.error('Failed to create document');
        }
      }
    } catch (error) {
      toast.error('Operation failed');
    } finally {
      setSavingDocument(false);
      setDocumentUploadProgress(0);
    }
  };

  const handleEditDocument = async () => {
    setSavingDocument(true);
    setDocumentUploadProgress(selectedDocumentFile ? 10 : 0);
    try {
      let response;

      if (selectedDocumentFile) {
        const uploadData = buildDocumentUploadFormData();

        setDocumentUploadProgress(55);
        response = await adminFetch(`/admin/documents/${selectedDocument.document_id}/replace-file`, {
          method: 'POST',
          credentials: 'include',
          body: uploadData,
        });
        setDocumentUploadProgress(90);
      } else {
        response = await adminFetch(`/admin/documents/${selectedDocument.document_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(documentFormData)
        });
      }

      if (response.ok) {
        toast.success(selectedDocumentFile ? 'Document updated and file replaced' : 'Document updated');
        setShowDocumentEditModal(false);
        setSelectedDocument(null);
        resetDocumentForm();
        fetchDocuments();
      } else {
        const error = await response.json().catch(() => ({}));
        if (selectedDocumentFile && response.status === 404) {
          toast.error('Replace-file route was not found. Restart the local backend and try again.');
        } else {
          toast.error(error.detail || 'Update failed');
        }
      }
    } catch (error) {
      toast.error('Update failed');
    } finally {
      setSavingDocument(false);
      setDocumentUploadProgress(0);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      const response = await adminFetch(`/admin/documents/${docId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Document deleted');
        fetchDocuments();
      }
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const openDocumentEditModal = (doc) => {
    setSelectedDocument(doc);
    setSelectedDocumentFile(null);
    setDocumentUploadProgress(0);
    setDocumentFormData({
      name: doc.name || '',
      description: doc.description || '',
      category: doc.category || 'general',
      access_level: doc.access_level || 'members',
      is_public: doc.is_public || false,
      tags: doc.tags || []
    });
    setShowDocumentEditModal(true);
  };

  const fetchPromptData = useCallback(async () => {
    try {
      const [historyRes, recordingsRes] = await Promise.all([
        adminFetch('/admin/prompts/history?weeks=12'),
        adminFetch('/recordings')
      ]);

      if (historyRes.ok) {
        const data = await historyRes.json();
        setPromptHistory(data);
      }
      if (recordingsRes.ok) {
        const data = await recordingsRes.json();
        setPromptRecordings(data || []);
        if (data && data.length > 0 && !selectedPromptRecording) {
          setSelectedPromptRecording(data[0].recording_id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setPromptsLoading(false);
    }
  }, [adminFetch, selectedPromptRecording]);

  const fetchPromptHistory = async () => {
    try {
      const response = await adminFetch('/admin/prompts/history?weeks=12');
      if (response.ok) {
        const data = await response.json();
        setPromptHistory(data);
      }
    } catch (error) {
      console.error('Failed to fetch prompt history:', error);
    }
  };

  const analyzePrompts = async () => {
    setAnalyzingPrompts(true);
    try {
      const response = await adminFetch('/admin/prompts/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        setPromptInsights(data);
        toast.success('Analysis complete!');
      } else {
        toast.error('Analysis failed');
      }
    } catch (error) {
      console.error('Failed to analyze prompts:', error);
      toast.error('Analysis failed');
    } finally {
      setAnalyzingPrompts(false);
    }
  };

  const handleGeneratePrompts = async () => {
    if (!selectedPromptRecording) {
      toast.error('Please select a recording first');
      return;
    }

    setPromptGenerating(true);
    try {
      const response = await adminFetch('/admin/generate-prompts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recording_id: selectedPromptRecording })
      });

      if (response.ok) {
        const data = await response.json();
        const formattedPrompts = (data.prompts || []).map((text, idx) => ({
          id: idx + 1,
          text: text
        }));
        setGeneratedPrompts(formattedPrompts);
        toast.success(`${formattedPrompts.length} prompts generated!`);
      } else {
        throw new Error('Generation failed');
      }
    } catch (error) {
      console.error('Failed to generate prompts:', error);
      const selectedRec = promptRecordings.find(r => r.recording_id === selectedPromptRecording);
      const focusArea = selectedRec?.focus_area || 'general';

      const fallbackPrompts = {
        marriage: [
          { id: 1, text: 'What is one area of your marriage where you need to show more patience this week?' },
          { id: 2, text: 'How can you create a moment of connection with your spouse today?' },
          { id: 3, text: 'What is something your wife has asked for that you have been putting off?' },
        ],
        parenting: [
          { id: 1, text: 'What is one way you can be more intentionally present with your children this week?' },
          { id: 2, text: 'How can you model faith for your kids in a practical way today?' },
          { id: 3, text: 'What value do you want to instill in your children and how will you demonstrate it?' },
        ],
        career: [
          { id: 1, text: 'How can you honor God in your work decisions this week?' },
          { id: 2, text: 'What boundary do you need to set to protect family time?' },
          { id: 3, text: 'How is your career serving your family rather than competing with it?' },
        ],
        general: [
          { id: 1, text: 'What is one thing you are grateful for as a father this week?' },
          { id: 2, text: 'How can you be more present with your family today?' },
          { id: 3, text: 'What is God teaching you in this season of fatherhood?' },
        ]
      };

      setGeneratedPrompts(fallbackPrompts[focusArea] || fallbackPrompts.general);
      toast.success('3 prompts generated!');
    } finally {
      setPromptGenerating(false);
    }
  };

  const handlePublishPrompt = async (prompt) => {
    setSelectedPrompt(prompt);
    try {
      const response = await adminFetch('/admin/publish-prompt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recording_id: selectedPromptRecording, prompt_text: prompt.text })
      });

      if (response.ok) {
        toast.success('Prompt published to community!');
        setGeneratedPrompts([]);
        fetchPromptHistory();
      } else {
        toast.error('Failed to publish prompt');
      }
    } catch (error) {
      toast.error('Failed to publish prompt');
    }
  };

  const handlePublishAllPrompts = async (prompts) => {
    try {
      const response = await adminFetch('/admin/publish-all-prompts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recording_id: selectedPromptRecording, prompts: prompts.map((prompt) => prompt.text || prompt) })
      });

      if (response.ok) {
        toast.success('All 3 prompts published for members to choose from!');
        setGeneratedPrompts([]);
        fetchPromptHistory();
      } else {
        toast.error('Failed to publish prompts');
      }
    } catch (error) {
      toast.error('Failed to publish prompts');
    }
  };

  const handleSaveRecordingReview = async (recordingId, payload) => {
    const response = await adminFetch(`/admin/recordings/${recordingId}/review`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to save recording review');
    }

    await fetchRecordings();
    return response.json();
  };

  const handlePublishRecordingFocus = async (recordingId, promptText, promptIndex = null, customNote = null, emailAudience = null) => {
    const response = await adminFetch('/admin/publish-prompt', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        recording_id: recordingId,
        prompt_text: promptText || undefined,
        prompt_index: promptIndex ?? undefined,
        custom_note: customNote || undefined,
        email_audience: emailAudience || undefined,
      })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to publish weekly focus');
    }

    await Promise.all([fetchRecordings(), fetchPromptHistory()]);
    return response.json();
  };

  const handleDeleteRecording = async (recordingId) => {
    const response = await adminFetch(`/recordings/${recordingId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to delete recording');
    }

    await Promise.all([fetchRecordings(), fetchPromptHistory()]);
    return response.json().catch(() => ({}));
  };

  useEffect(() => {
    fetchOverviewData();
  }, [fetchOverviewData]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  useEffect(() => {
    fetchFeedback();
  }, [fetchFeedback]);

  useEffect(() => {
    if (activeTab !== 'guestpasses' && activeTab !== COMMAND_CENTER_HOME_TAB && activeRoom.id !== 'reach-dads') return;
    fetchGuestPasses();
  }, [activeRoom.id, activeTab, fetchGuestPasses]);

  useEffect(() => {
    if (activeTab !== COMMAND_CENTER_HOME_TAB && activeTab !== 'guestguides' && activeRoom.id !== 'guide-partners') return;
    fetchGuideSummary();
  }, [activeRoom.id, activeTab, fetchGuideSummary]);

  useEffect(() => {
    fetchEmailHistory();
  }, [fetchEmailHistory]);

  useEffect(() => {
    fetchEmailAnalytics();
  }, [fetchEmailAnalytics]);

  useEffect(() => {
    fetchSavedEmailDrafts();
  }, [fetchSavedEmailDrafts]);

  useEffect(() => {
    fetchSavedEmailTemplates();
  }, [fetchSavedEmailTemplates]);

  useEffect(() => {
    fetchScheduledEmails();
  }, [fetchScheduledEmails]);

  useEffect(() => {
    fetchEmailDeliveryIssues();
  }, [fetchEmailDeliveryIssues]);

  useEffect(() => {
    fetchFeaturePlanning();
  }, [fetchFeaturePlanning]);

  useEffect(() => {
    if (activeTab !== 'pricing') return;
    setPricingLoading(true);
    fetchCoupons();
  }, [activeTab, fetchCoupons]);

  useEffect(() => {
    if (activeTab !== 'supporters' && activeTab !== COMMAND_CENTER_HOME_TAB && activeRoom.id !== 'support-partners') return;
    fetchSupportOverview();
  }, [activeRoom.id, activeTab, fetchSupportOverview]);

  useEffect(() => {
    if (activeTab !== 'advisorypartneraccounts' && activeTab !== COMMAND_CENTER_HOME_TAB && activeRoom.id !== 'advisory-partners') return;
    fetchAdvisoryOverview();
  }, [activeRoom.id, activeTab, fetchAdvisoryOverview]);

  useEffect(() => {
    if (activeTab !== 'superfanaccounts' && activeTab !== COMMAND_CENTER_HOME_TAB && activeRoom.id !== 'super-fans') return;
    fetchSuperFanOverview();
  }, [activeRoom.id, activeTab, fetchSuperFanOverview]);

  useEffect(() => {
    if (activeTab !== 'resources') return;
    setResourcesLoading(true);
    fetchResources();
  }, [activeTab, fetchResources]);

  useEffect(() => {
    if (
      activeTab !== 'members'
      && activeTab !== COMMAND_CENTER_HOME_TAB
      && activeRoom.id !== 'reach-dads'
      && activeRoom.id !== 'support-dads'
    ) return;
    fetchMembers();
  }, [activeRoom.id, activeTab, fetchMembers]);

  useEffect(() => {
    if (activeTab !== 'recordings') return;
    fetchRecordings();
  }, [activeTab, fetchRecordings]);

  useEffect(() => {
    if (activeTab !== 'documents' && activeRoom.id !== 'system-health') return;
    fetchDocuments();
  }, [activeRoom.id, activeTab, fetchDocuments]);

  useEffect(() => {
    if (activeTab !== 'monitoring') return;
    setSystemHealthLoading(true);
    fetchSystemHealthOverview();
  }, [activeTab, fetchSystemHealthOverview]);

  useEffect(() => {
    if (activeTab !== 'prompts') return;
    fetchPromptData();
  }, [activeTab, fetchPromptData]);

  const getPageHeading = () => {
    if (isCommandCenterView) {
      return {
        title: 'Command Center',
        description: 'Operate Dad Partners by mission first, then drill into the tools underneath.',
        zoneTitle: null,
        roomTitle: null,
        toolLabel: null,
        helperLabel: null,
      };
    }

    return {
      title: activeRoom.title,
      description: activeRoom.question || activeZone.question,
      zoneTitle: activeZone.title,
      roomTitle: activeRoom.title,
      toolLabel: activeTool?.label || null,
      helperLabel: activeRoom.supportingLine || activeZone.supportingLine || null,
    };
  };

  const pageHeading = getPageHeading();
  const pageHeadingPills = useMemo(() => {
    const items = [
      pageHeading.zoneTitle
        ? {
            key: 'zone',
            label: pageHeading.zoneTitle,
            className: 'border-aqua/25 bg-aqua/10 text-cyan-800 dark:border-aqua/20 dark:bg-aqua/10 dark:text-aqua',
          }
        : null,
      pageHeading.roomTitle
        ? {
            key: 'room',
            label: pageHeading.roomTitle,
            className: 'border-slate-200 bg-slate-100 text-slate-700 dark:border-white/10 dark:bg-white/[0.04] dark:text-gray-200',
          }
        : null,
      pageHeading.toolLabel
        ? {
            key: 'tool',
            label: pageHeading.toolLabel,
            className: 'border-cyan-200 bg-cyan-50 text-cyan-800 dark:border-aqua/20 dark:bg-aqua/10 dark:text-aqua',
          }
        : null,
    ].filter(Boolean);

    const seen = new Set();
    return items.filter((item) => {
      if (seen.has(item.label)) return false;
      seen.add(item.label);
      return true;
    });
  }, [pageHeading.roomTitle, pageHeading.toolLabel, pageHeading.zoneTitle]);
  const heartWorkGuide = useMemo(() => {
    if (isCommandCenterView || activeZone?.id !== 'heart-work') return null;

    if (activeRoom?.id === 'listen-learn') {
        return {
          eyebrow: 'Start here',
          title: 'Listen first, then decide what needs care',
          detail: `You are in ${activeTool?.label || 'Feedback'} inside Feedback & Insight. Use this room to hear what dads are carrying, spot the next real pattern, and only then move into Dad Partners support work.`,
        };
      }

    if (activeRoom?.id === 'support-dads') {
      if (activeTab === 'members' || activeTab === 'email') {
        return {
          eyebrow: 'Dad Partners flow',
          title: 'Work the dad relationship first, then move the support that fits',
          detail: `Use this room to decide who needs personal follow-up, who should hear from you next, and only then move into feedback, recordings, prompts, or resources when they will actually help.`,
        };
      }

      return {
        eyebrow: 'Dad Partners flow',
        title: 'Move the support that matches the need',
        detail: 'Use this room after the need is clear, then choose the one recording, resource, or support move that should happen next.',
      };
    }

    return null;
  }, [activeRoom?.id, activeTab, activeTool?.label, activeZone?.id, isCommandCenterView]);
  const siteSettingsStatusItems = useMemo(() => {
    if (activeTab !== 'sitesettings') return [];

    const events = Array.isArray(siteSettings?.upcoming_events) ? siteSettings.upcoming_events : [];
    const now = Date.now();
    const futureEvents = events.filter((event) => {
      const startTime = event?.start_datetime ? new Date(event.start_datetime).getTime() : NaN;
      const endTime = event?.end_datetime ? new Date(event.end_datetime).getTime() : NaN;
      const comparisonTime = Number.isFinite(endTime) ? endTime : startTime;
      return Number.isFinite(comparisonTime) && comparisonTime >= now;
    });
    const pastEvents = events.filter((event) => {
      const startTime = event?.start_datetime ? new Date(event.start_datetime).getTime() : NaN;
      const endTime = event?.end_datetime ? new Date(event.end_datetime).getTime() : NaN;
      const comparisonTime = Number.isFinite(endTime) ? endTime : startTime;
      return Number.isFinite(comparisonTime) && comparisonTime < now;
    });
    const launchItems = Array.isArray(siteSettings?.launch_critical_path?.items)
      ? siteSettings.launch_critical_path.items.filter((item) => item?.enabled !== false)
      : [];
    const founding40Ready = Boolean(siteSettings?.founding_40_start_date && siteSettings?.founding_40_time);
    const founding40SeriesDates = Array.isArray(siteSettings?.founding_40_series_dates)
      ? siteSettings.founding_40_series_dates.filter(Boolean)
      : [];
    const introVideoReady = Boolean(String(siteSettings?.intro_video_url || '').trim());

    return [
      {
        key: 'zoom',
        label: 'Zoom link',
        status: siteSettings?.zoom_link ? 'Ready' : 'Missing',
        tone: siteSettings?.zoom_link ? 'ready' : 'attention',
        detail: siteSettings?.zoom_link ? 'Launch call link is set.' : 'Add the live call link before launch.',
      },
      {
        key: 'events',
        label: 'Upcoming events',
        status: futureEvents.length > 0 ? 'Ready' : 'Needs setup',
        tone: futureEvents.length > 0 ? 'ready' : 'attention',
        detail: futureEvents.length > 0
          ? `${pluralize(futureEvents.length, 'future event')} scheduled`
          : 'No future event is configured yet.',
      },
      {
        key: 'critical-path',
        label: 'Current Focus',
        status: launchItems.length > 0 ? 'Loaded' : 'Needs review',
        tone: launchItems.length > 0 ? 'ready' : 'attention',
        detail: launchItems.length > 0
          ? `${pluralize(launchItems.length, 'active priority item')} in the stack`
          : 'Current Focus items still need to be set intentionally.',
      },
      {
        key: 'founding-40',
        label: 'Founding 40',
        status: founding40Ready ? 'Ready' : 'Needs setup',
        tone: founding40Ready ? 'ready' : 'attention',
        detail: founding40Ready
          ? `${founding40SeriesDates.length || 1} date${(founding40SeriesDates.length || 1) === 1 ? '' : 's'} starting ${siteSettings.founding_40_start_date} at ${siteSettings.founding_40_time}`
          : 'Set the weekly call date and time.',
      },
      {
        key: 'intro-video',
        label: 'Intro video',
        status: introVideoReady ? 'Ready' : 'Optional',
        tone: introVideoReady ? 'ready' : 'attention',
        detail: introVideoReady
          ? 'Homepage introduction video is configured.'
          : 'Add the welcome video when it is ready.',
      },
      {
        key: 'past-events',
        label: 'Past events',
        status: pastEvents.length > 0 ? 'Review' : 'Clean',
        tone: pastEvents.length > 0 ? 'attention' : 'ready',
        detail: pastEvents.length > 0
          ? `${pluralize(pastEvents.length, 'past event')} still listed in settings`
          : 'No past events are lingering in the list.',
      },
    ];
  }, [activeTab, siteSettings]);

  if (!authLoading && (!isAuthenticated || !isAdmin || !hasToken)) {
    return (
      <div className="min-h-screen bg-charcoal-dark">
        <Header
          isLoggedIn={!!user}
          user={user}
          onOpenSettings={() => setActiveTab('sitesettings', 'system-health')}
        />
        <div className="pt-16">
          <div className="max-w-7xl mx-auto px-4 py-8">
            <Card className="border-border bg-card p-6">
              <h2 className="mb-2 text-lg font-semibold text-foreground">Not authorized</h2>
              <p className="text-sm text-muted-foreground">
                Your account does not currently have access to the Admin Dashboard.
              </p>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-charcoal-dark">
      <Header
        isLoggedIn={!!user}
        user={user}
        onOpenSettings={() => setActiveTab('sitesettings', 'system-health')}
      />
      
      <div className="pt-16">
        <div ref={roomRailRef} className="fixed left-0 right-0 top-16 z-40 border-b border-charcoal-light bg-charcoal/95 backdrop-blur">
          <div className="max-w-7xl mx-auto px-4 py-2.5">
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  if (isCommandCenterView) {
                    navigate('/');
                    return;
                  }
                  openCommandCenter();
                }}
                className="p-2 text-slate-500 transition-colors hover:text-slate-900 dark:text-gray-400 dark:hover:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="min-w-0 flex-1 overflow-x-auto scrollbar-hide">
                <div className="flex min-w-max items-center gap-1.5 pr-2">
                  <button
                    type="button"
                    onClick={openCommandCenter}
                    className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-[12px] font-medium transition-colors ${
                      isCommandCenterView
                        ? 'border-aqua bg-aqua text-charcoal'
                        : 'border-slate-700 bg-charcoal-light text-gray-300 hover:border-aqua/40 hover:text-white'
                    }`}
                  >
                    <Grid className="h-3 w-3" />
                    <span>Command Center</span>
                  </button>

                  {ADMIN_ZONES.map((zone) => {
                    const Icon = zone.icon;
                    const isActive = !isCommandCenterView && activeZone.id === zone.id;
                    const zoneContext = zoneContextsById[zone.id];
                    const roomDotClass = ROOM_PILL_DOT_CLASSES[zoneContext?.statusTone] || ROOM_PILL_DOT_CLASSES.steady;

                    return (
                      <button
                        key={zone.id}
                        type="button"
                        onClick={() => openZone(zone.id)}
                        aria-current={isActive ? 'page' : undefined}
                        className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-[12px] font-medium transition-colors ${
                          isActive
                            ? ACTIVE_ZONE_PILL_CLASS
                            : 'border-slate-700 bg-charcoal-light text-gray-300 hover:border-aqua/40 hover:text-white'
                        }`}
                      >
                        <Icon className="h-3 w-3" />
                        <span>{zone.title}</span>
                        <span className={`h-2 w-2 rounded-full ${roomDotClass}`} />
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="h-[52px]" />

        <div className="max-w-7xl mx-auto px-4 py-3">

          {!isCommandCenterView && activeRoom && activeTab !== 'monitoring' ? (
            <div ref={activeRoomSectionRef} className="mb-3 space-y-3">
              {!hasScopedMembersView ? (
                <div className="fixed left-0 right-0 top-[124px] z-30">
                  <div className="max-w-7xl mx-auto px-4">
                    <div ref={activeRoomHeaderRef} className="rounded-2xl border border-border bg-card p-3 shadow-[0_4px_16px_-4px_rgba(0,0,0,0.4)] backdrop-blur-sm">
                      <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                        <div className="min-w-0 lg:flex lg:flex-wrap lg:items-center lg:gap-x-2 lg:gap-y-1">
                          <div className="min-w-0">
                            {!isCommandCenterView ? (
                              <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
                                {pageHeadingPills.map((pill, index) => (
                                  <React.Fragment key={pill.key}>
                                    {index > 0 ? (
                                    <span className="text-xs font-semibold text-slate-400 dark:text-gray-500">
                                      /
                                    </span>
                                    ) : null}
                                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.18em] ${pill.className}`}>
                                      {pill.label}
                                    </span>
                                  </React.Fragment>
                                ))}
                              </div>
                            ) : null}
                            <p className="text-lg font-semibold text-slate-900 dark:text-white">
                              {pageHeading.title}
                            </p>
                            {pageHeading.description ? (
                              <p className="mt-0.5 text-sm text-slate-600 dark:text-gray-400 lg:mt-0">
                                {pageHeading.description}
                              </p>
                            ) : null}
                            {pageHeading.helperLabel ? (
                              <p className="mt-1.5 text-xs font-medium tracking-[0.02em] text-slate-500 dark:text-gray-400">
                                {pageHeading.helperLabel}
                              </p>
                            ) : null}
                          </div>
                        </div>

                        <div className="flex flex-col gap-2 lg:items-end">
                        <div className="flex flex-wrap gap-1.5 lg:max-w-xl lg:justify-end">
                            {activeRoom?.title && activeRoom.title !== activeZone.title ? (
                              <div className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 text-[12px] text-slate-700 dark:border-white/10 dark:bg-white/[0.04] dark:text-gray-200">
                                <span className="font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-gray-400">
                                  Room
                                </span>
                                <span className="font-medium text-slate-900 dark:text-white">
                                  {activeRoom.title}
                                </span>
                              </div>
                            ) : null}
                            {activeZone.tools.map((tool) => {
                              const ToolIcon = tool.icon;
                              const isToolActive = activeTab === tool.tab;
                              const showPendingReviewCount = tool.tab === 'recordings' && pendingFocusReviewCount > 0;
                              const showGuideAttentionCount = tool.tab === 'guestguides' && guidePendingAttentionCount > 0;

                              return (
                                <button
                                  key={`${activeZone.id}-${tool.tab}`}
                                  type="button"
                                  onClick={() => setActiveTab(
                                    tool.tab,
                                    tool.roomId || activeRoom.id,
                                    tool.tab === 'guestguides'
                                      ? { workflowKey: 'guide-partner-outreach' }
                                      : null
                                  )}
                                  aria-current={isToolActive ? 'page' : undefined}
                                  className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[12px] transition-colors ${
                                    isToolActive
                                      ? 'border-aqua bg-aqua text-charcoal shadow-[0_12px_24px_-18px_rgba(34,211,238,0.9)]'
                                      : 'border-slate-200 bg-white text-slate-700 hover:border-aqua/40 hover:text-slate-900 dark:border-border dark:bg-card dark:text-gray-300 dark:hover:border-aqua/30 dark:hover:text-white'
                                  }`}
                                >
                                  <ToolIcon className="h-3 w-3" />
                                  {tool.label}
                                  {showPendingReviewCount ? (
                                    <span className={`inline-flex min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-semibold ${
                                      isToolActive
                                        ? 'bg-charcoal/15 text-charcoal'
                                        : 'bg-yellow-500/15 text-yellow-700 dark:text-yellow-300'
                                    }`}>
                                      {pendingFocusReviewCount}
                                    </span>
                                  ) : null}
                                  {showGuideAttentionCount ? (
                                    <span className={`inline-flex min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-semibold ${
                                      isToolActive
                                        ? 'bg-charcoal/15 text-charcoal'
                                        : guideAttention.counts.onboardingCompleted > 0
                                          ? 'bg-teal-500/15 text-teal-700 dark:text-teal-300'
                                          : 'bg-orange-500/15 text-orange-700 dark:text-orange-300'
                                    }`}>
                                      {guidePendingAttentionCount}
                                    </span>
                                  ) : null}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              {!hasScopedMembersView ? (
                <div aria-hidden="true" style={{ height: activeRoomHeaderHeight }} />
              ) : null}

              {heartWorkGuide && !hasScopedMembersView && !suppressDadPartnersLandingChrome ? (
                <div className="rounded-2xl border border-cyan-100 bg-cyan-50/70 px-4 py-3 dark:border-aqua/25 dark:bg-slate-900/80">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div className="max-w-3xl">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-cyan-800 dark:text-aqua">
                        {heartWorkGuide.eyebrow}
                      </p>
                      <p className="mt-1.5 text-sm font-semibold text-slate-900 dark:text-white">
                        {heartWorkGuide.title}
                      </p>
                      <p className="mt-1.5 text-sm leading-6 text-slate-600 dark:text-gray-200">
                        {heartWorkGuide.detail}
                      </p>
                    </div>
                    {pageHeading.toolLabel ? (
                      <div className="inline-flex w-fit items-center rounded-full border border-cyan-200 bg-white/80 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-800 dark:border-aqua/30 dark:bg-slate-950/60 dark:text-aqua">
                        Current tool: {pageHeading.toolLabel}
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {activeTab === activeRoom.primaryTab && activeTab !== 'monitoring' && !isToolFirstTab && !hasScopedMembersView && !suppressDadPartnersLandingChrome ? (
                <ActiveRoomContext context={roomContextsById[activeRoom.id] || activeZoneContext} onOpenTool={setActiveTab} />
              ) : null}
            </div>
          ) : null}

          <div ref={toolContentRef} className="scroll-mt-32">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsContent value="overview">
              <div ref={landingContentRef}>
                <CommandCenterLanding
                  zones={ADMIN_ZONES}
                  stats={overviewStats}
                  activity={overviewActivity}
                  loading={overviewLoading}
                  notAuthorized={overviewNotAuthorized}
                  founding40Status={overviewFounding40Status}
                  analytics={analyticsData}
                  atRiskMembers={analyticsAtRiskMembers}
                  feedbackItems={feedbackItems}
                  guideSummary={guideSummary}
                  supportOverview={supportOverview}
                  advisoryOverview={advisoryOverview}
                  superFanOverview={superFanOverview}
                  sentGuestPasses={sentGuestPasses}
                  siteSettings={siteSettings}
                  pendingFocusReviewCount={pendingFocusReviewCount}
                  commandCenterQueue={queueState}
                  commandCenterQueueLoading={queueLoading}
                  commandCenterQueueRefreshing={queueRefreshing}
                  commandCenterQueueError={queueError}
                  commandCenterQueueCreating={creatingQueueItem}
                  commandCenterQueueMutatingIds={commandCenterQueueMutatingIds}
                  onRefreshQueue={refreshQueue}
                  onCreateQueueItem={createQueueItem}
                  onUpdateQueueItem={updateQueueItem}
                  onResolveQueueItem={resolveQueueItem}
                  onDeferQueueItem={deferQueueItem}
                  onReviewQueueItem={reviewQueueItem}
                  onDismissQueueItem={dismissQueueItem}
                  onDeleteQueueItem={deleteQueueItem}
                  onOpenZone={openZone}
                  onOpenTool={(roomId, tab, toolContext = null) => setActiveTab(tab, roomId, toolContext)}
                />
              </div>
            </TabsContent>
            <TabsContent value="analytics">
              <AnalyticsTab
                timeRange={analyticsTimeRange}
                setTimeRange={handleAnalyticsTimeRangeChange}
                analytics={analyticsData}
                atRiskMembers={analyticsAtRiskMembers}
                onboardingAnalytics={analyticsOnboarding}
                weeklyRhythmAnalytics={analyticsWeeklyRhythm}
                loading={analyticsLoading}
                activeZone={activeZone}
                onOpenTool={setActiveTab}
                adminFetch={adminFetch}
              />
            </TabsContent>
            <TabsContent value="featureplanning">
              <FeaturePlanningTab
                items={featurePlanningItems}
                loading={featurePlanningLoading}
                saving={featurePlanningSaving}
                editingItem={featurePlanningEditing}
                onSave={handleSaveFeaturePlanning}
                onStartEdit={setFeaturePlanningEditing}
                onCancelEdit={() => setFeaturePlanningEditing(null)}
                onAnalyze={handleAnalyzeFeaturePlanning}
                analysisLoadingId={featurePlanningAnalysisLoadingId}
              />
            </TabsContent>
            <TabsContent value="feedback">
              <FeedbackTab
                loading={feedbackLoading}
                feedbackItems={feedbackItems}
                setFeedbackItems={setFeedbackItems}
                aiInsights={feedbackAiInsights}
                analyzing={feedbackAnalyzing}
                runAIAnalysis={runFeedbackAIAnalysis}
              />
            </TabsContent>
            <TabsContent value="gems">
              <GemsTab />
            </TabsContent>
            <TabsContent value="peersupport">
              <PeerSupportTab />
            </TabsContent>
            <TabsContent value="prompts">
              <PromptsTab
                generating={promptGenerating}
                prompts={generatedPrompts}
                selectedRecording={selectedPromptRecording}
                setSelectedRecording={setSelectedPromptRecording}
                recordings={promptRecordings}
                promptHistory={promptHistory}
                loading={promptsLoading}
                analyzingPrompts={analyzingPrompts}
                promptInsights={promptInsights}
                handleGenerate={handleGeneratePrompts}
                handlePublish={handlePublishPrompt}
                handlePublishAll={handlePublishAllPrompts}
                analyzePrompts={analyzePrompts}
                getFocusAreaLabel={getFocusAreaLabel}
                openRecordingsReview={() => setActiveTab('recordings')}
              />
            </TabsContent>
            <TabsContent value="members">
              <MembersTab
                adminFetch={adminFetch}
                {...membersState}
                members={membersState.members || []}
                loading={membersState.membersLoading}
                founding40PublicMode={siteSettings?.founding_40_public_mode || 'join'}
                defaultTierFilter={activeZone?.id === 'heart-work' ? 'member' : activeZone?.id === 'communication' ? 'prospect' : 'all'}
                activeZoneId={activeZone?.id || null}
                activeRoomId={activeRoom?.id || null}
                targetMemberIds={activeTab === 'members' ? activeToolContext?.targetMemberIds || null : null}
                targetScopeLabel={activeTab === 'members' ? activeToolContext?.targetScopeLabel || null : null}
                targetRequestKey={activeTab === 'members' ? activeToolContext?.requestKey || null : null}
              />
            </TabsContent>
            <TabsContent value="recordings">
              <RecordingsTab
                viewMode={recordingsViewMode}
                setViewMode={setRecordingsViewMode}
                focusFilter={recordingsFocusFilter}
                setFocusFilter={setRecordingsFocusFilter}
                analyzingRecordings={analyzingRecordings}
                recordingInsights={recordingInsights}
                recordings={recordings}
                loading={recordingsLoading}
                uploading={uploadingRecording}
                uploadProgress={recordingUploadProgress}
                showUploadModal={showRecordingUploadModal}
                setShowUploadModal={setShowRecordingUploadModal}
                uploadForm={recordingUploadForm}
                setUploadForm={setRecordingUploadForm}
                selectedFile={selectedRecordingFile}
                setSelectedFile={setSelectedRecordingFile}
                fileInputRef={recordingFileInputRef}
                focusAreaOrder={FOCUS_AREA_ORDER}
                getFocusAreaLabel={getFocusAreaLabel}
                handleFileSelect={handleRecordingFileSelect}
                handleUpload={handleRecordingUpload}
                runRecordingAnalysis={runRecordingAnalysis}
                handleSaveReview={handleSaveRecordingReview}
                handlePublishFocus={handlePublishRecordingFocus}
                handleDeleteRecording={handleDeleteRecording}
                handleReplaceRecordingMedia={handleReplaceRecordingMedia}
                replacingRecordingMediaId={replacingRecordingMediaId}
              />
            </TabsContent>
            <TabsContent value="resources">
              <ResourcesTab
                newResource={newResource}
                setNewResource={setNewResource}
                adding={addingResource}
                analyzingResources={analyzingResources}
                resourceInsights={resourceInsights}
                resources={resources}
                loading={resourcesLoading}
                editingResource={editingResource}
                setEditingResource={setEditingResource}
                editForm={resourceEditForm}
                setEditForm={setResourceEditForm}
                handleAddResource={handleAddResource}
                handleStartEdit={handleStartEditResource}
                handleSaveEdit={handleSaveResourceEdit}
                handleDeleteResource={handleDeleteResource}
                runResourceAnalysis={runResourceAnalysis}
              />
            </TabsContent>
            <TabsContent value="pricing">
              <PricingTab
                prices={pricingPrices}
                setPrices={setPricingPrices}
                coupons={pricingCoupons}
                showCouponModal={pricingShowCouponModal}
                setShowCouponModal={setPricingShowCouponModal}
                newCoupon={pricingNewCoupon}
                setNewCoupon={setPricingNewCoupon}
                loading={pricingLoading}
                creating={pricingCreating}
                handleCreateCoupon={handleCreateCoupon}
                handleDeleteCoupon={handleDeleteCoupon}
              />
            </TabsContent>
            <TabsContent value="membershipenrollments">
              <MembershipEnrollmentsTab adminFetch={adminFetch} />
            </TabsContent>
            <TabsContent value="supporters">
              <SupportersTab
                overview={supportOverview}
                loading={supportLoading}
                resyncingIds={supportResyncingIds}
                onRefresh={fetchSupportOverview}
                onResync={handleResyncSupporter}
              />
            </TabsContent>
            <TabsContent value="supportpartneraccounts">
              <SupportPartnerAccountsTab adminFetch={adminFetch} />
            </TabsContent>
            <TabsContent value="marketingcontent">
              <MarketingContentTab
                adminFetch={adminFetch}
                externalDraft={marketingQueueDraft}
                onExternalDraftConsumed={clearMarketingQueueDraft}
              />
            </TabsContent>
            <TabsContent value="marketingintelligence">
              <MarketingIntelligenceTab
                adminFetch={adminFetch}
                onAddToQueue={handleMarketingQueuePrefill}
              />
            </TabsContent>
            <TabsContent value="marketingplaylist">
              <MarketingPlaylistTab
                adminFetch={adminFetch}
                onAddToQueue={handleMarketingQueuePrefill}
              />
            </TabsContent>
            <TabsContent value="freeuserscomingsoon">
              <ComingSoonTab
                eyebrow="Free Users lane"
                title="Free Users is visible now, but the activation workflow is still pending"
                description="This lane will hold free-user and unactivated-account operations once the real product flow is live. It stays visible now so the admin shell reflects the intended Command Center shape."
                highlights={[
                  'Free-tier account visibility',
                  'Activation and nurture workflow',
                  'Conversion and upgrade follow-through',
                ]}
              />
            </TabsContent>
            <TabsContent value="advisorypartneraccounts">
              <AdvisoryPartnerAccountsTab adminFetch={adminFetch} />
            </TabsContent>
            <TabsContent value="superfanaccounts">
              <SuperFanAccountsTab adminFetch={adminFetch} />
            </TabsContent>
            <TabsContent value="supportcontent">
              <SupportPartnerContent adminFetch={adminFetch} />
            </TabsContent>
            <TabsContent value="advisorycontent">
              <AdvisoryContent adminFetch={adminFetch} />
            </TabsContent>
            <TabsContent value="guestpasses">
              <GuestPassesTab
                adminFetch={adminFetch}
                email={guestPassEmail}
                setEmail={setGuestPassEmail}
                name={guestPassName}
                setName={setGuestPassName}
                sending={guestPassSending}
                sentPasses={sentGuestPasses}
                loading={guestPassesLoading}
                handleSendGuestPass={handleSendGuestPass}
                fetchSentPasses={fetchGuestPasses}
                copyCode={copyGuestPassCode}
                isExpired={isGuestPassExpired}
              />
            </TabsContent>
            <TabsContent value="guestguides">
              <GuestGuidesTab
                adminFetch={adminFetch}
                authLoading={authLoading}
                hasAdminToken={Boolean(hasToken)}
                activeCampaigns={guideSummary?.activeCampaigns || []}
                deliveryIssues={emailDeliveryIssues}
                guideSummary={guideSummary}
                refreshGuideSummary={fetchGuideSummary}
                workflowKey={activeTab === 'guestguides' ? activeToolContext?.workflowKey || null : null}
                workflowRequestKey={activeTab === 'guestguides' ? activeToolContext?.requestKey || null : null}
              />
            </TabsContent>
            <TabsContent value="email">
              <EmailTab
                emailContent={emailContent}
                setEmailContent={setEmailContent}
                emailHeroUploading={emailHeroUploading}
                handleUploadEmailHero={handleUploadEmailHero}
                sending={emailSending}
                emailTestSending={emailTestSending}
                emailScheduling={emailScheduling}
                emailScheduleAt={emailScheduleAt}
                setEmailScheduleAt={setEmailScheduleAt}
                emailPreview={emailPreview}
                emailPreviewOpen={emailPreviewOpen}
                setEmailPreviewOpen={setEmailPreviewOpen}
                previewingEmail={emailPreviewLoading}
                recentEmails={recentEmails}
                loading={emailLoading}
                emailAnalytics={emailAnalytics}
                emailAnalyticsLoading={emailAnalyticsLoading}
                deliveryIssues={emailDeliveryIssues}
                deliveryIssueCounts={emailDeliveryIssueCounts}
                deliveryIssuesLoading={emailDeliveryIssuesLoading}
                deliveryIssuesImporting={emailDeliveryIssuesImporting}
                deliveryIssuesLastImportedAt={emailDeliveryIssuesLastImportedAt}
                handleImportDeliveryIssues={handleImportEmailDeliveryIssues}
                handlePreviewEmail={handlePreviewEmail}
                handleSendTestEmail={handleSendTestEmail}
                handleScheduleEmail={handleScheduleEmail}
                scheduledEmails={scheduledEmails}
                scheduledEmailsLoading={scheduledEmailsLoading}
                handleCancelScheduledEmail={handleCancelScheduledEmail}
                handleSendEmail={handleSendEmail}
                emailDraftAssist={emailDraftAssist}
                setEmailDraftAssist={setEmailDraftAssist}
                generatingEmailDraft={emailDraftGenerating}
                handleGenerateEmailDraft={handleGenerateEmailDraft}
                savedEmailDrafts={savedEmailDrafts}
                savedEmailDraftsLoading={savedEmailDraftsLoading}
                emailDraftSaving={emailDraftSaving}
                handleSaveEmailDraft={handleSaveEmailDraft}
                handleLoadEmailDraft={handleLoadEmailDraft}
                handleDeleteEmailDraft={handleDeleteEmailDraft}
                savedEmailTemplates={savedEmailTemplates}
                savedEmailTemplatesLoading={savedEmailTemplatesLoading}
                emailTemplateSaving={emailTemplateSaving}
                handleSaveEmailTemplate={handleSaveEmailTemplate}
                handleLoadEmailTemplate={handleLoadEmailTemplate}
                handleDeleteEmailTemplate={handleDeleteEmailTemplate}
                activeZone={activeZone}
                onOpenTool={setActiveTab}
              />
            </TabsContent>
            <TabsContent value="monitoring">
              <MonitoringTab
                overview={systemHealthOverview}
                loading={systemHealthLoading}
                runningProductionChecks={systemHealthRunningProductionChecks}
                runningLocalChecks={systemHealthRunningLocalChecks}
                historyHours={systemHealthHistoryHours}
                setHistoryHours={setSystemHealthHistoryHours}
                targetAnchor={activeTab === 'monitoring' ? activeToolContext?.targetAnchor || null : null}
                targetRecordId={activeTab === 'monitoring' ? activeToolContext?.targetRecordId || null : null}
                targetRequestKey={activeTab === 'monitoring' ? activeToolContext?.requestKey || null : null}
                onRefresh={() => {
                  setSystemHealthLoading(true);
                  fetchSystemHealthOverview();
                }}
                onRunProductionChecks={runProductionSystemHealthChecks}
                onRunLocalChecks={runLocalSystemHealthChecks}
              />
            </TabsContent>
            <TabsContent value="documents">
              <DocumentsTab
                adminFetch={adminFetch}
                documents={documents}
                availableTags={documentAvailableTags}
                categoryFilter={documentCategoryFilter}
                setCategoryFilter={setDocumentCategoryFilter}
                loading={documentsLoading}
                showAddModal={showDocumentAddModal}
                setShowAddModal={setShowDocumentAddModal}
                showEditModal={showDocumentEditModal}
                setShowEditModal={setShowDocumentEditModal}
                selectedDoc={selectedDocument}
                setSelectedDoc={setSelectedDocument}
                saving={savingDocument}
                searchTerm={documentSearchTerm}
                setSearchTerm={setDocumentSearchTerm}
                selectedFile={selectedDocumentFile}
                setSelectedFile={setSelectedDocumentFile}
                dragOver={documentDragOver}
                setDragOver={setDocumentDragOver}
                newTag={documentNewTag}
                setNewTag={setDocumentNewTag}
                uploadProgress={documentUploadProgress}
                fileInputRef={documentFileInputRef}
                formData={documentFormData}
                setFormData={setDocumentFormData}
                resetForm={resetDocumentForm}
                handleFileSelect={handleDocumentFileSelect}
                handleDrop={handleDocumentDrop}
                toggleTag={toggleDocumentTag}
                addCustomTag={addDocumentCustomTag}
                handleAddDocument={handleAddDocument}
                handleEditDocument={handleEditDocument}
                handleDeleteDocument={handleDeleteDocument}
                openEditModal={openDocumentEditModal}
              />
            </TabsContent>
            <TabsContent value="sitesettings">
              <SiteSettingsTab
                {...siteSettingsTabProps}
                statusItems={siteSettingsStatusItems}
                targetAnchor={activeTab === 'sitesettings' ? activeToolContext?.targetAnchor || null : null}
                targetRecordId={activeTab === 'sitesettings' ? activeToolContext?.targetRecordId || null : null}
                targetRequestKey={activeTab === 'sitesettings' ? activeToolContext?.requestKey || null : null}
              />
            </TabsContent>
          </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== SITE SETTINGS TAB ====================
export default AdminDashboard;
